// ─── INSTALL DEPS BEFORE USING ────────────────────────────────────────────────
// npm install jspdf jspdf-autotable
// ──────────────────────────────────────────────────────────────────────────────

import React, { useState, useEffect, useRef } from "react";
import Navbar from "../components/Navbar";
import { Helmet } from "react-helmet-async";
import { db } from "../firebase/config";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import emailjs from "@emailjs/browser";
import PageTransition from "../components/PageTransition";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

const EMAILJS_SERVICE_ID = "service_kporv3e";
const EMAILJS_TEMPLATE_ID = "template_gxllwch";
const EMAILJS_PUBLIC_KEY = "1ya1P7Zs3dwMpoZ8k";

// ─── HELPERS ──────────────────────────────────────────────────────────────────
function php(n) {
  return "PHP " + Number(n).toLocaleString("en-PH");
}

function stripEmoji(str) {
  if (!str) return "";
  return str
    .replace(
      /[\u{1F000}-\u{1FFFF}\u{2600}-\u{27BF}\u{2300}-\u{23FF}\u{FE00}-\u{FE0F}\u{1F900}-\u{1F9FF}\u{1FA00}-\u{1FA9F}\u{200D}\u{FE0F}]/gu,
      "",
    )
    .replace(/\s{2,}/g, " ")
    .trim();
}

function groupFeatures(list) {
  const groups = {};
  list.forEach((f) => {
    if (!groups[f.group]) groups[f.group] = [];
    groups[f.group].push(f);
  });
  return groups;
}

function getIncludedFeatureIds(projectId) {
  if (!projectId || !FEATURES[projectId]) return [];
  return FEATURES[projectId].filter((f) => f.cost === 0).map((f) => f.id);
}

function isValidEmail(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
}

// ─── MUTEX GROUPS ─────────────────────────────────────────────────────────────
// Features within the same array are mutually exclusive (radio-button behaviour).
// Selecting one automatically deselects the others in the same group.
const MUTEX_GROUPS = {
  website: [
    ["pages10", "pagesUnlim"], // page-tier upgrades — only one allowed
  ],
};

// ─── PROJECT TYPES ────────────────────────────────────────────────────────────
const PROJECT_TYPES = [
  {
    id: "website",
    icon: "🌐",
    label: "Website",
    base: 60000,
    desc: "Business websites with content management",
    disabled: false,
    includes: [
      "Up to 3 pages (Home, About, Contact)",
      "Basic responsive layout",
      "Simple contact form",
      "Static content only (no CMS)",
      "Source code turnover",
    ],
  },
  {
    id: "ecommerce",
    icon: "🛒",
    label: "E-Commerce Store",
    base: 100000,
    desc: "Online store with payment integrations",
    disabled: false,
    includes: [
      "Up to 10 product listings",
      "Basic product listing & detail pages",
      "Simple cart & checkout flow",
      "Cash on delivery payment only",
      "Basic order list (admin view)",
      "Source code turnover",
    ],
  },
  {
    id: "booking",
    icon: "📅",
    label: "Booking System",
    base: 60000,
    desc: "Appointment & scheduling platform",
    disabled: false,
    includes: [
      "Single-service booking form",
      "Basic date & time slot picker",
      "Admin booking list & status view",
      "Basic email confirmation (template)",
      "Source code turnover",
    ],
  },
  {
    id: "business",
    icon: "🏢",
    label: "Business System",
    base: 300000,
    desc: "Operations & workflow management",
    disabled: false,
    includes: [
      "Core admin dashboard (overview only)",
      "Basic user login & 2-role access",
      "Simple data entry & records module",
      "Basic list views with search",
      "Source code turnover",
    ],
  },
  {
    id: "school",
    icon: "🏫",
    label: "School Management",
    base: 250000,
    desc: "Enrollment, grading & campus platform",
    disabled: false,
    includes: [
      "Basic student enrollment form",
      "Manual grade entry (single subject)",
      "Simple attendance logging",
      "Admin dashboard (basic)",
      "Source code turnover",
    ],
  },
  {
    id: "lgu",
    icon: "🏛️",
    label: "LGU / Government",
    base: 220000,
    desc: "Digital government services platform",
    disabled: false,
    includes: [
      "Basic citizen request form (single transaction type)",
      "Simple document status tracker (admin-side only)",
      "Admin dashboard (basic overview)",
      "Single-department user login",
      "Source code turnover",
    ],
  },
  {
    id: "mobile",
    icon: "📱",
    label: "Mobile App",
    base: 150000,
    desc: "Android / cross-platform mobile app",
    disabled: true,
    disabledNote:
      "Coming Soon — We're currently not accepting mobile app projects.",
    includes: [],
  },
  {
    id: "custom",
    icon: "⚙️",
    label: "Custom Software",
    base: 200000,
    desc: "Tailored system built from scratch",
    disabled: false,
    includes: [
      "User login & authentication",
      "Single admin role (no role hierarchy)",
      "Basic admin dashboard (layout only)",
      "Simple data entry & list view (1 module)",
      "Source code turnover",
    ],
  },
];

// ─── FEATURES ─────────────────────────────────────────────────────────────────
const FEATURES = {
  website: [
    {
      id: "pages5",
      group: "Design & Pages",
      label: "Up to 5 Pages",
      cost: 0,
      desc: "Your site includes up to 5 professionally designed pages, covering the essential sections of your business such as Home, About, Services, and Contact.",
    },
    {
      id: "pages10",
      group: "Design & Pages",
      label: "Up to 10 Pages (+5)",
      cost: 15000,
      desc: "Expands your site to up to 10 pages, giving more room for service sub-pages, portfolios, team profiles, or additional landing pages.",
    },
    {
      id: "pagesUnlim",
      group: "Design & Pages",
      label: "Unlimited Pages",
      cost: 30000,
      desc: "No page limit — build as many pages as your business requires, ideal for large companies with extensive product or service offerings.",
    },
    {
      id: "customUI",
      group: "Design & Pages",
      label: "Custom UI/UX Design",
      cost: 20000,
      desc: "A fully bespoke interface designed from scratch to match your brand's colors, typography, and identity — ensuring a unique and polished look.",
    },
    {
      id: "responsive",
      group: "Design & Pages",
      label: "Mobile & Tablet Responsive",
      cost: 8000,
      desc: "Your website automatically adapts its layout for all screen sizes — desktop, tablet, and mobile — ensuring a seamless experience for every visitor.",
    },
    {
      id: "darkmode",
      group: "Design & Pages",
      label: "Dark / Light Mode Toggle",
      cost: 8000,
      desc: "Adds a theme toggle button that lets visitors switch between a light and dark mode interface, improving usability and reducing eye strain.",
    },
    {
      id: "cms",
      group: "Content Management",
      label: "CMS Admin Panel",
      cost: 15000,
      desc: "A user-friendly admin panel that lets you add, edit, and delete website content without any coding knowledge or developer assistance.",
    },
    {
      id: "blog",
      group: "Content Management",
      label: "Blog / News Module",
      cost: 12000,
      desc: "A complete blogging system with post creation, categories, tags, and publishing controls — great for SEO and keeping your audience engaged.",
    },
    {
      id: "medialib",
      group: "Content Management",
      label: "Media Library Management",
      cost: 8000,
      desc: "A centralized media manager for uploading, organizing, and reusing images and files across your website from a single dashboard.",
    },
    {
      id: "banners",
      group: "Content Management",
      label: "Banner & Slider Management",
      cost: 6000,
      desc: "Allows admins to update homepage banners and image sliders directly from the dashboard without requiring developer involvement.",
    },
    {
      id: "events",
      group: "Content Management",
      label: "Events & Announcements",
      cost: 10000,
      desc: "A dedicated section to post upcoming events, announcements, or news updates that are visible to all site visitors.",
    },
    {
      id: "faq",
      group: "Content Management",
      label: "FAQ / Knowledge Base",
      cost: 6000,
      desc: "An organized FAQ section where admins can manage questions and answers, helping visitors quickly find the information they need.",
    },
    {
      id: "testimonials",
      group: "Content Management",
      label: "Testimonials Module",
      cost: 5000,
      desc: "Display client testimonials and reviews in a visually appealing format to build trust and credibility with new visitors.",
    },
    {
      id: "multiuser",
      group: "Users & Access",
      label: "Multi-User Role Management",
      cost: 18000,
      desc: "Supports multiple admin accounts with different roles and permission levels, allowing safe and organized team collaboration.",
    },
    {
      id: "adminAudit",
      group: "Users & Access",
      label: "Admin Activity Logs",
      cost: 10000,
      desc: "Tracks every action taken by admin users — logins, edits, and deletions — providing a full accountability trail.",
    },
    {
      id: "twofa",
      group: "Users & Access",
      label: "Two-Factor Authentication",
      cost: 10000,
      desc: "Adds a second security layer requiring admins to verify their identity via email or authenticator app during login.",
    },
    {
      id: "contact",
      group: "Communications",
      label: "Contact / Inquiry Form",
      cost: 5000,
      desc: "A form that allows site visitors to send inquiries directly to your team with automatic email delivery upon submission.",
    },
    {
      id: "emailNotif",
      group: "Communications",
      label: "Email Notification System",
      cost: 8000,
      desc: "Automated email notifications triggered by key actions — new inquiry, form submissions, or user registrations — sent instantly.",
    },
    {
      id: "livechat",
      group: "Communications",
      label: "Live Chat Integration",
      cost: 8000,
      desc: "Integrates a real-time live chat widget so visitors can talk to your team instantly without leaving the website.",
    },
    {
      id: "newsletter",
      group: "Communications",
      label: "Newsletter Subscription",
      cost: 8000,
      desc: "Lets visitors subscribe to your mailing list, with admin tools to manage subscribers and broadcast email campaigns.",
    },
    {
      id: "seo",
      group: "Integrations & SEO",
      label: "Advanced SEO Configuration",
      cost: 10000,
      desc: "Advanced SEO setup including meta tags, Open Graph, sitemap generation, and structured data for better search engine rankings.",
    },
    {
      id: "analytics",
      group: "Integrations & SEO",
      label: "Google Analytics Integration",
      cost: 6000,
      desc: "Connects your site to Google Analytics to track visitor behavior, traffic sources, and page performance in real time.",
    },
    {
      id: "maps",
      group: "Integrations & SEO",
      label: "Google Maps Integration",
      cost: 5000,
      desc: "Embeds an interactive Google Map on your contact or location page so clients can easily find your physical location.",
    },
    {
      id: "social",
      group: "Integrations & SEO",
      label: "Social Media Integration",
      cost: 6000,
      desc: "Links to your social media profiles and adds share buttons, helping visitors follow you and share your content with ease.",
    },
    {
      id: "multilang",
      group: "Integrations & SEO",
      label: "Multi-Language Support",
      cost: 20000,
      desc: "Full support for multiple languages with a language switcher, making your website accessible to international audiences.",
    },
    {
      id: "ssl",
      group: "Security & Hosting",
      label: "SSL & Security Configuration",
      cost: 5000,
      desc: "Configures your SSL certificate for HTTPS, encrypting all data between your server and visitors for security and better SEO.",
    },
    {
      id: "backup",
      group: "Security & Hosting",
      label: "Automated Backup System",
      cost: 8000,
      desc: "Sets up automated daily or weekly backups of your database and files, protecting your data from accidental loss.",
    },
    {
      id: "hosting",
      group: "Security & Hosting",
      label: "Hosting & Deployment Assistance",
      cost: 10000,
      desc: "Handles deployment of your website to a live server, including environment setup, DNS configuration, and go-live support.",
    },
  ],
  ecommerce: [
    {
      id: "catalog",
      group: "Store Setup",
      label: "Product Catalog & Categories",
      cost: 15000,
      desc: "A full product catalog with organized categories and subcategories, making it easy for customers to browse your offerings.",
    },
    {
      id: "variants",
      group: "Store Setup",
      label: "Product Variants (size/color/etc)",
      cost: 10000,
      desc: "Supports multiple product variations like size, color, or material — each with its own price, stock level, and product image.",
    },
    {
      id: "unlimited",
      group: "Store Setup",
      label: "Unlimited Product Listings",
      cost: 12000,
      desc: "Remove the product listing cap and add as many products as your store requires without any restrictions.",
    },
    {
      id: "bulkImport",
      group: "Store Setup",
      label: "Bulk Product Import (CSV)",
      cost: 10000,
      desc: "Upload hundreds of products at once using a formatted CSV spreadsheet, saving significant time on manual data entry.",
    },
    {
      id: "customUI",
      group: "Store Setup",
      label: "Custom Storefront UI/UX Design",
      cost: 20000,
      desc: "A fully custom-designed storefront tailored to your brand — beyond standard templates, built to convert visitors into buyers.",
    },
    {
      id: "gcash",
      group: "Payments",
      label: "GCash Payment Integration",
      cost: 15000,
      desc: "Integrates GCash as a payment method, allowing customers to pay directly via their GCash wallet at checkout.",
    },
    {
      id: "maya",
      group: "Payments",
      label: "Maya Payment Integration",
      cost: 15000,
      desc: "Integrates Maya (formerly PayMaya) as a payment option, supporting wallet and linked card payments from customers.",
    },
    {
      id: "card",
      group: "Payments",
      label: "Credit/Debit Card Payment",
      cost: 22000,
      desc: "Enables customers to pay using Visa, Mastercard, and other credit/debit cards via a secure PCI-compliant payment gateway.",
    },
    {
      id: "bank",
      group: "Payments",
      label: "Bank Transfer / Manual Payment",
      cost: 5000,
      desc: "Adds a manual bank transfer payment option with payment confirmation flow and admin verification before order processing.",
    },
    // ── FIX: cost changed from 5000 → 0 because the base package already
    //         explicitly includes "Cash on delivery payment only". Charging
    //         extra for something already in the base is a double-count.
    {
      id: "cod",
      group: "Payments",
      label: "Cash on Delivery Option",
      cost: 0,
      desc: "Cash on delivery — customers pay when the order arrives, with delivery tracking and COD confirmation managed in-system.",
    },
    {
      id: "installment",
      group: "Payments",
      label: "Installment / Buy Now Pay Later",
      cost: 18000,
      desc: "Offers buy now, pay later or installment payment options through supported gateways, lowering the barrier to purchase.",
    },
    {
      id: "inventory",
      group: "Inventory & Orders",
      label: "Inventory Management System",
      cost: 14000,
      desc: "Full inventory tracking with stock levels, adjustments, and history for every product and variant to prevent overselling.",
    },
    {
      id: "lowstock",
      group: "Inventory & Orders",
      label: "Low Stock Alerts",
      cost: 6000,
      desc: "Automatically sends alerts when a product's stock level falls below a configurable threshold to prevent stockouts.",
    },
    // ── FIX: cost changed from 12000 → 0 because the base package already
    //         includes "Simple cart & checkout flow". The base entry and this
    //         feature described the same capability, creating a double-charge.
    {
      id: "cart",
      group: "Inventory & Orders",
      label: "Shopping Cart & Checkout",
      cost: 0,
      desc: "A complete shopping cart and checkout flow with item management, quantity updates, and a full order summary before payment.",
    },
    {
      id: "orders",
      group: "Inventory & Orders",
      label: "Order Management Dashboard",
      cost: 10000,
      desc: "A comprehensive admin dashboard to view, manage, update, and fulfill all customer orders in one centralized place.",
    },
    {
      id: "tracking",
      group: "Inventory & Orders",
      label: "Order Tracking & Status Updates",
      cost: 10000,
      desc: "Lets customers track the real-time status of their orders — from pending through processing, shipped, and delivered.",
    },
    {
      id: "returns",
      group: "Inventory & Orders",
      label: "Returns & Refund Management",
      cost: 12000,
      desc: "Handles return requests, refund approvals, and inventory restocking for returned items through a structured workflow.",
    },
    {
      id: "accounts",
      group: "Customers",
      label: "Customer Accounts & Profiles",
      cost: 10000,
      desc: "Customers can create accounts to save their addresses, track their orders, and manage their shopping preferences.",
    },
    {
      id: "history",
      group: "Customers",
      label: "Order History",
      cost: 6000,
      desc: "Customers can view their full purchase history with order details, itemized receipts, and current delivery status.",
    },
    {
      id: "wishlist",
      group: "Customers",
      label: "Wishlist / Save for Later",
      cost: 6000,
      desc: "Lets customers save products they like for future purchase, encouraging return visits and reducing abandoned carts.",
    },
    {
      id: "reviews",
      group: "Customers",
      label: "Product Reviews & Ratings",
      cost: 8000,
      desc: "Customers can leave star ratings and written reviews on products, boosting social proof and helping buyer decisions.",
    },
    {
      id: "promo",
      group: "Marketing",
      label: "Promo Codes & Discount Engine",
      cost: 10000,
      desc: "Create and manage discount codes with custom rules — percentage or fixed amount, minimum spend, usage limits, and expiry dates.",
    },
    {
      id: "flashsale",
      group: "Marketing",
      label: "Flash Sale Module",
      cost: 10000,
      desc: "Set up limited-time flash sales with countdown timers and discounted prices to drive urgency and quick purchasing decisions.",
    },
    {
      id: "loyalty",
      group: "Marketing",
      label: "Loyalty Points & Rewards",
      cost: 20000,
      desc: "Rewards repeat customers with points earned on every purchase, redeemable for discounts or free items to encourage loyalty.",
    },
    {
      id: "abandoned",
      group: "Marketing",
      label: "Abandoned Cart Recovery",
      cost: 12000,
      desc: "Automatically sends follow-up emails to customers who added items to their cart but did not complete the checkout.",
    },
    {
      id: "referral",
      group: "Marketing",
      label: "Referral Program",
      cost: 12000,
      desc: "A referral program that rewards existing customers with credits or discounts for inviting new buyers through unique referral links.",
    },
    {
      id: "emailNotif",
      group: "Notifications",
      label: "Email Order Notifications",
      cost: 8000,
      desc: "Sends automated email notifications to customers for order confirmation, shipping updates, and delivery completion.",
    },
    {
      id: "sms",
      group: "Notifications",
      label: "SMS Notifications",
      cost: 10000,
      desc: "Sends SMS notifications at key order stages — confirmation, dispatch, and delivery — keeping customers informed in real time.",
    },
    {
      id: "analytics",
      group: "Analytics",
      label: "Sales Analytics Dashboard",
      cost: 15000,
      desc: "A visual dashboard showing sales performance, revenue trends, best-selling products, and customer insights over time.",
    },
    {
      id: "reports",
      group: "Analytics",
      label: "Inventory & Revenue Reports",
      cost: 10000,
      desc: "Generates detailed inventory and revenue reports exportable to Excel or PDF for offline analysis and business planning.",
    },
    {
      id: "shipping",
      group: "Logistics",
      label: "Shipping Fee Calculator",
      cost: 8000,
      desc: "Calculates shipping fees at checkout based on customer location, item weight, or flat rate rules defined by the admin.",
    },
    {
      id: "multiwarehouse",
      group: "Logistics",
      label: "Multi-Warehouse Management",
      cost: 20000,
      desc: "Manage stock across multiple warehouse locations with separate inventory levels and intelligent fulfillment routing.",
    },
  ],
  booking: [
    {
      id: "form",
      group: "Core Booking",
      label: "Online Booking Form",
      cost: 8000,
      desc: "A clean and intuitive online booking form where clients can enter their details and request appointments easily from any device.",
    },
    {
      id: "calendar",
      group: "Core Booking",
      label: "Real-Time Availability Calendar",
      cost: 12000,
      desc: "Displays real-time availability so clients only see open time slots, preventing double bookings and scheduling conflicts.",
    },
    {
      id: "walkin",
      group: "Core Booking",
      label: "Walk-In Booking Support",
      cost: 6000,
      desc: "Allows staff to manually record walk-in appointments directly in the system alongside online bookings for unified scheduling.",
    },
    {
      id: "recurring",
      group: "Core Booking",
      label: "Recurring Appointments",
      cost: 10000,
      desc: "Supports recurring appointment scheduling — daily, weekly, or monthly — for clients with regular service visit patterns.",
    },
    {
      id: "groupBook",
      group: "Core Booking",
      label: "Group / Party Booking",
      cost: 10000,
      desc: "Enables booking for multiple people or group events under a single appointment entry with total headcount tracking.",
    },
    {
      id: "multistaff",
      group: "Staff & Services",
      label: "Multi-Staff Management",
      cost: 14000,
      desc: "Manage multiple staff members, each with their own individual schedule, assigned services, and booking availability.",
    },
    {
      id: "services",
      group: "Staff & Services",
      label: "Service Menu Management",
      cost: 8000,
      desc: "A configurable service menu with names, durations, prices, and staff assignments that clients can select during booking.",
    },
    {
      id: "schedules",
      group: "Staff & Services",
      label: "Staff Schedule & Shift Management",
      cost: 12000,
      desc: "Defines working hours and shift schedules for each staff member, controlling the exact time windows they are bookable.",
    },
    {
      id: "breaks",
      group: "Staff & Services",
      label: "Staff Breaks & Days-Off Control",
      cost: 8000,
      desc: "Allows staff to block off break times, lunch hours, or specific days off so clients cannot book during those unavailable periods.",
    },
    {
      id: "commission",
      group: "Staff & Services",
      label: "Staff Commission Tracking",
      cost: 10000,
      desc: "Tracks commissions earned by each staff member per service rendered or booking completed, for payroll and performance review.",
    },
    {
      id: "clientProfile",
      group: "Clients",
      label: "Client Profile & History",
      cost: 10000,
      desc: "Stores full client profiles including contact information, service preferences, and complete visit history for personalized service.",
    },
    {
      id: "notes",
      group: "Clients",
      label: "Client Notes / Medical Records",
      cost: 8000,
      desc: "Attach private service notes or medical history records to individual client profiles for reference during future appointments.",
    },
    {
      id: "loyalty",
      group: "Clients",
      label: "Client Loyalty & VIP Tracking",
      cost: 12000,
      desc: "Tracks client visit frequency and rewards loyal or VIP clients with special perks, discounts, or priority booking access.",
    },
    {
      id: "emailConfirm",
      group: "Notifications",
      label: "Email Confirmation",
      cost: 6000,
      desc: "Automatically sends a detailed booking confirmation email to clients immediately after they successfully schedule an appointment.",
    },
    {
      id: "smsReminder",
      group: "Notifications",
      label: "SMS Reminders",
      cost: 10000,
      desc: "Sends automated SMS reminders to clients a set number of hours before their appointment time to significantly reduce no-shows.",
    },
    {
      id: "autoReminder",
      group: "Notifications",
      label: "Automated Follow-Up Messages",
      cost: 8000,
      desc: "Sends follow-up messages after appointments — thank-you notes, rebooking prompts, or satisfaction surveys automatically.",
    },
    {
      id: "payment",
      group: "Payments",
      label: "Online Payment & Deposit Collection",
      cost: 18000,
      desc: "Allows clients to pay in full or leave a deposit at the time of booking to secure their appointment slot.",
    },
    {
      id: "gcash",
      group: "Payments",
      label: "GCash / Maya Integration",
      cost: 15000,
      desc: "Accepts GCash and Maya payments for online deposits or full appointment payment directly at the time of booking.",
    },
    {
      id: "refund",
      group: "Payments",
      label: "Refund & Cancellation Fees",
      cost: 8000,
      desc: "Manages refund requests and enforces cancellation fee policies based on how far in advance the client cancels.",
    },
    {
      id: "reschedule",
      group: "Management",
      label: "Rescheduling & Cancellation Module",
      cost: 8000,
      desc: "Lets clients or admins reschedule or cancel appointments through the system, with automated notifications sent to both parties.",
    },
    {
      id: "waitlist",
      group: "Management",
      label: "Waitlist & Queue Management",
      cost: 8000,
      desc: "Automatically adds interested clients to a waitlist when their preferred time is full, notifying them when a slot opens up.",
    },
    {
      id: "multibranch",
      group: "Management",
      label: "Multi-Branch / Location Management",
      cost: 20000,
      desc: "Manage bookings across multiple business locations, each with its own staff, schedule, and independent availability calendar.",
    },
    {
      id: "promo",
      group: "Management",
      label: "Promo & Package Deals",
      cost: 10000,
      desc: "Create and apply promotional deals or bundled service packages that clients can select and redeem during the booking process.",
    },
    {
      id: "gcal",
      group: "Integrations",
      label: "Google Calendar Sync",
      cost: 8000,
      desc: "Syncs confirmed appointments with Google Calendar so staff always have an up-to-date view of their schedule in one place.",
    },
    {
      id: "reports",
      group: "Integrations",
      label: "Booking Analytics & Reports",
      cost: 10000,
      desc: "Provides detailed booking analytics including appointment volume, revenue earned, peak booking times, and staff performance data.",
    },
    {
      id: "customUI",
      group: "Integrations",
      label: "Custom Branded Booking Page",
      cost: 15000,
      desc: "A branded, client-facing booking page fully customized to match your business identity for a professional first impression.",
    },
  ],
  business: [
    {
      id: "dashboard",
      group: "Core Operations",
      label: "Operations Dashboard",
      cost: 20000,
      desc: "A real-time operations overview dashboard displaying key business metrics, recent activity feeds, and system-wide alerts.",
    },
    {
      id: "inventory",
      group: "Core Operations",
      label: "Inventory & Product Management",
      cost: 20000,
      desc: "Full inventory management with product entries, stock levels, supplier information, and reorder tracking in one module.",
    },
    {
      id: "pos",
      group: "Core Operations",
      label: "POS / Transaction Module",
      cost: 25000,
      desc: "A point-of-sale module for recording sales transactions, generating printed receipts, and handling cash or digital payments.",
    },
    {
      id: "procurement",
      group: "Core Operations",
      label: "Procurement & Purchase Orders",
      cost: 20000,
      desc: "Handles the creation, multi-level approval, and tracking of purchase orders from request through delivery confirmation.",
    },
    {
      id: "supplier",
      group: "Core Operations",
      label: "Supplier Management",
      cost: 15000,
      desc: "Maintains a supplier directory with contact details, payment terms, delivery lead times, and complete purchase history.",
    },
    {
      id: "assets",
      group: "Core Operations",
      label: "Asset & Equipment Tracking",
      cost: 15000,
      desc: "Track all physical assets and equipment in your business, including location, condition, assigned user, and maintenance schedule.",
    },
    {
      id: "billing",
      group: "Finance",
      label: "Billing & Invoice Generation",
      cost: 18000,
      desc: "Generate professional invoices and billing statements for clients with full payment tracking and outstanding balance monitoring.",
    },
    {
      id: "accounting",
      group: "Finance",
      label: "Basic Accounting Module",
      cost: 25000,
      desc: "A basic accounting module for tracking income, expenses, and generating simplified financial summaries and statements.",
    },
    {
      id: "payroll",
      group: "Finance",
      label: "Payroll Processing",
      cost: 35000,
      desc: "Automates payroll computation including basic pay, allowances, government deductions (SSS, PhilHealth, Pag-IBIG), and net pay per employee.",
    },
    {
      id: "expense",
      group: "Finance",
      label: "Expense Tracking",
      cost: 12000,
      desc: "Logs and categorizes all business expenses for easy tracking, approval workflows, and inclusion in financial reports.",
    },
    {
      id: "budgeting",
      group: "Finance",
      label: "Budget Planning Module",
      cost: 15000,
      desc: "Define and monitor budget allocations per department or project, with real-time comparison against actual spending.",
    },
    {
      id: "hr",
      group: "HR & Staff",
      label: "HR & Employee Management",
      cost: 30000,
      desc: "Manages the complete employee lifecycle — personal records, contracts, document submissions, and HR event history.",
    },
    {
      id: "attendance",
      group: "HR & Staff",
      label: "Employee Attendance & DTR",
      cost: 15000,
      desc: "Tracks daily employee attendance and time-in/out records, generating DTR summaries for payroll and monitoring purposes.",
    },
    {
      id: "leave",
      group: "HR & Staff",
      label: "Leave & Absence Management",
      cost: 10000,
      desc: "Manages employee leave applications with approval workflows, leave type balances, and historical absence records.",
    },
    {
      id: "performance",
      group: "HR & Staff",
      label: "Performance Evaluation Module",
      cost: 15000,
      desc: "Structured employee performance evaluation system with KPI scoring, supervisor feedback, and periodic review history.",
    },
    {
      id: "recruitment",
      group: "HR & Staff",
      label: "Recruitment & Applicant Tracking",
      cost: 15000,
      desc: "Tracks job postings, applicant submissions, interview stages, and hiring decisions in a single organized pipeline.",
    },
    {
      id: "approval",
      group: "Workflows",
      label: "Approval & Routing Workflows",
      cost: 20000,
      desc: "Configurable multi-step approval workflows for requests, documents, or financial transactions that require sequential sign-off.",
    },
    {
      id: "tasks",
      group: "Workflows",
      label: "Task & Project Management",
      cost: 18000,
      desc: "Assign, track, and manage tasks across teams with deadlines, priority levels, status updates, and progress visibility.",
    },
    {
      id: "documents",
      group: "Workflows",
      label: "Document Management System",
      cost: 15000,
      desc: "A centralized document system for uploading, versioning, organizing, and sharing internal files across your organization.",
    },
    {
      id: "crm",
      group: "Workflows",
      label: "CRM / Customer Management",
      cost: 20000,
      desc: "Manage client relationships, track interaction history, schedule follow-ups, and monitor your sales pipeline in one place.",
    },
    {
      id: "reports",
      group: "Reporting",
      label: "Custom Reports & Analytics",
      cost: 18000,
      desc: "Build and generate custom reports from any module with flexible filtering, grouping, and export capabilities.",
    },
    {
      id: "kpi",
      group: "Reporting",
      label: "KPI & Executive Dashboard",
      cost: 20000,
      desc: "An executive-level dashboard displaying your most critical key performance indicators and business metrics at a glance.",
    },
    {
      id: "export",
      group: "Reporting",
      label: "Data Export (Excel/PDF)",
      cost: 8000,
      desc: "Export any data table or report as an Excel spreadsheet or PDF document for offline use or external stakeholder sharing.",
    },
    {
      id: "roles",
      group: "Security & Branches",
      label: "Role-Based Access Control",
      cost: 15000,
      desc: "Define granular permissions per user role — precisely controlling who can view, add, edit, or delete specific data and modules.",
    },
    {
      id: "audit",
      group: "Security & Branches",
      label: "Audit Logs & Activity Monitoring",
      cost: 15000,
      desc: "Logs every user action in the system — logins, edits, deletions — with timestamps and user identity for full accountability.",
    },
    {
      id: "multibranch",
      group: "Security & Branches",
      label: "Multi-Branch Management",
      cost: 25000,
      desc: "Manage business operations across multiple branch locations with consolidated reporting and branch-level access controls.",
    },
    {
      id: "api",
      group: "Integrations",
      label: "3rd Party API Integration",
      cost: 25000,
      desc: "Connects your system to external services or existing business software through REST API integration for seamless data flow.",
    },
    {
      id: "sms",
      group: "Integrations",
      label: "SMS Notification System",
      cost: 10000,
      desc: "Sends automated SMS notifications for approvals, alerts, or reminders directly to designated user mobile numbers.",
    },
    {
      id: "email",
      group: "Integrations",
      label: "Email Notification System",
      cost: 8000,
      desc: "Sends automated email alerts and notifications triggered by key events and actions within the system.",
    },
  ],
  school: [
    {
      id: "enrollment",
      group: "Enrollment",
      label: "Student Enrollment System",
      cost: 25000,
      desc: "A complete student enrollment system with form submission, document checklist upload, and real-time enrollment status tracking.",
    },
    {
      id: "admission",
      group: "Enrollment",
      label: "Online Admission & Application",
      cost: 20000,
      desc: "An online admission portal where prospective students can submit applications, upload required documents, and monitor their application status.",
    },
    {
      id: "sectioning",
      group: "Enrollment",
      label: "Section & Subject Assignment",
      cost: 12000,
      desc: "Automated or manual assignment of enrolled students to sections and subjects based on curriculum requirements and year level.",
    },
    {
      id: "curriculum",
      group: "Enrollment",
      label: "Curriculum Management",
      cost: 15000,
      desc: "Manage and configure complete curriculum structures per course, year level, and semester including subject sequencing.",
    },
    {
      id: "schoolyr",
      group: "Enrollment",
      label: "School Year & Semester Setup",
      cost: 8000,
      desc: "Set up and switch between school years and semesters, controlling which data is active, editable, or locked for each period.",
    },
    {
      id: "grading",
      group: "Academics",
      label: "Grading System & Grade Entry",
      cost: 25000,
      desc: "A full grading system where teachers encode component grades and the system automatically computes final grades based on your formula.",
    },
    {
      id: "reportcard",
      group: "Academics",
      label: "Report Card Generation",
      cost: 20000,
      desc: "Automatically generates printable or PDF report cards per student per grading period, following your school's official format.",
    },
    {
      id: "gwa",
      group: "Academics",
      label: "GWA & Honor Roll Computation",
      cost: 12000,
      desc: "Automatically computes each student's General Weighted Average and determines honor roll standings based on set criteria.",
    },
    {
      id: "tor",
      group: "Academics",
      label: "Transcript of Records (TOR)",
      cost: 15000,
      desc: "Generates the official Transcript of Records for graduating or transferring students with complete academic history.",
    },
    {
      id: "subjects",
      group: "Academics",
      label: "Subject & Class Management",
      cost: 12000,
      desc: "Manage subject offerings including subject codes, units, assigned teachers, class sizes, and active school year associations.",
    },
    {
      id: "schedule",
      group: "Academics",
      label: "Class Schedule Builder",
      cost: 15000,
      desc: "A visual class schedule builder that assigns subjects, classrooms, and teachers to time slots without conflicts or overlaps.",
    },
    {
      id: "attendance",
      group: "Attendance",
      label: "Student Attendance Monitoring",
      cost: 15000,
      desc: "Monitors daily student attendance per class with absence and tardiness tracking, parent alerts, and comprehensive attendance reports.",
    },
    {
      id: "rfid",
      group: "Attendance",
      label: "RFID / QR Code Attendance",
      cost: 25000,
      desc: "Uses RFID cards or student QR codes to automatically log check-in and check-out attendance, eliminating manual encoding.",
    },
    {
      id: "staffAttend",
      group: "Attendance",
      label: "Teacher / Staff Attendance",
      cost: 12000,
      desc: "Tracks teacher and non-teaching staff attendance separately, with time-in/out records integrated with payroll computation.",
    },
    {
      id: "billing",
      group: "Billing & Finance",
      label: "Tuition Fee Billing",
      cost: 20000,
      desc: "Generates detailed tuition fee billing statements per student per semester, with an itemized breakdown of all applicable fees.",
    },
    {
      id: "payment",
      group: "Billing & Finance",
      label: "Online Payment Integration",
      cost: 20000,
      desc: "Allows parents or students to pay tuition and other fees online through integrated payment gateways like GCash or credit card.",
    },
    {
      id: "discount",
      group: "Billing & Finance",
      label: "Scholarship & Discount Management",
      cost: 10000,
      desc: "Manages scholarship grants and fee discounts — tracking recipients, discount amounts, and applicable subjects or semesters.",
    },
    {
      id: "payroll",
      group: "Billing & Finance",
      label: "Teacher & Staff Payroll",
      cost: 30000,
      desc: "Processes monthly payroll for all teaching and non-teaching staff including salary, allowances, deductions, and net pay generation.",
    },
    {
      id: "ledger",
      group: "Billing & Finance",
      label: "Student Ledger & Balance Tracking",
      cost: 12000,
      desc: "Tracks each student's running financial ledger — payments received, outstanding balances, and complete billing transaction history.",
    },
    {
      id: "studentPort",
      group: "Portals",
      label: "Student Portal",
      cost: 20000,
      desc: "A dedicated login portal for students to view their grades, attendance records, class schedules, and school announcements.",
    },
    {
      id: "parentPort",
      group: "Portals",
      label: "Parent / Guardian Portal",
      cost: 20000,
      desc: "A parent-facing portal for monitoring their child's academic performance, attendance, billing balance, and school communications.",
    },
    {
      id: "facultyPort",
      group: "Portals",
      label: "Faculty Portal",
      cost: 20000,
      desc: "A teacher portal for encoding grades, viewing assigned class lists, managing attendance records, and posting announcements.",
    },
    {
      id: "adminPort",
      group: "Portals",
      label: "Admin Portal & Dashboard",
      cost: 15000,
      desc: "A comprehensive admin dashboard for managing all school operations — enrollment, academics, finance, and staff — in one place.",
    },
    {
      id: "library",
      group: "Library & Facilities",
      label: "Library Management System",
      cost: 20000,
      desc: "A library system for cataloging books and resources, managing borrowing and return transactions, and tracking overdue items.",
    },
    {
      id: "inventory",
      group: "Library & Facilities",
      label: "School Supplies Inventory",
      cost: 12000,
      desc: "Tracks school supplies, textbooks, and equipment with stock levels, issuance records, and automatic reorder alerts.",
    },
    {
      id: "transport",
      group: "Library & Facilities",
      label: "School Bus & Transport Tracking",
      cost: 20000,
      desc: "Manages school bus routes, assigned students per route, driver information, and optional real-time GPS tracking.",
    },
    {
      id: "canteen",
      group: "Library & Facilities",
      label: "Canteen / Cashless Payment",
      cost: 15000,
      desc: "A cashless payment system for the school canteen using reloadable student ID cards or QR codes for quick transactions.",
    },
    {
      id: "announce",
      group: "Communication",
      label: "Announcements & Bulletin Board",
      cost: 8000,
      desc: "An in-system announcement board where admins can post bulletins visible to targeted groups — students, parents, or faculty.",
    },
    {
      id: "messaging",
      group: "Communication",
      label: "Internal Messaging System",
      cost: 15000,
      desc: "An internal messaging system enabling direct communication between admins, teachers, students, and parents within the platform.",
    },
    {
      id: "sms",
      group: "Communication",
      label: "SMS Notifications to Parents",
      cost: 10000,
      desc: "Sends SMS notifications to registered parent mobile numbers for attendance alerts, balance reminders, or important announcements.",
    },
    {
      id: "lms",
      group: "Learning (LMS)",
      label: "Learning Management System",
      cost: 50000,
      desc: "A full Learning Management System with course modules, lesson content, file uploads, assignments, and student progress tracking per subject.",
    },
    {
      id: "exam",
      group: "Learning (LMS)",
      label: "Online Examination Module",
      cost: 20000,
      desc: "Online examination system with question banks, timed exams, automatic scoring, and detailed result reporting per student.",
    },
    {
      id: "homework",
      group: "Learning (LMS)",
      label: "Homework / Assignment Submission",
      cost: 15000,
      desc: "Allows teachers to post assignments and students to submit answers or file uploads within the deadline set by the teacher.",
    },
    {
      id: "cert",
      group: "Certificates",
      label: "Certificate Generation",
      cost: 10000,
      desc: "Generates printable certificates for academic completion, honor awards, extracurricular participation, or recognition events.",
    },
    {
      id: "id",
      group: "Certificates",
      label: "School ID Generation",
      cost: 8000,
      desc: "Auto-generates student and staff ID cards with photos, full name, year level, and school branding, ready for printing.",
    },
    {
      id: "diploma",
      group: "Certificates",
      label: "Diploma / Completion Records",
      cost: 10000,
      desc: "Creates and archives official diplomas and completion certificates for graduating students with secure record keeping.",
    },
  ],
  lgu: [
    {
      id: "citizenPortal",
      group: "Citizen Services",
      label: "Citizen Services Portal",
      cost: 30000,
      desc: "A public-facing portal where residents can access government services, file requests, and monitor transaction status online.",
    },
    {
      id: "onlineReq",
      group: "Citizen Services",
      label: "Online Request & Transactions",
      cost: 25000,
      desc: "Enables citizens to submit requests for documents and services online without needing to physically visit the LGU office.",
    },
    {
      id: "queueing",
      group: "Citizen Services",
      label: "Queue / Ticketing System",
      cost: 15000,
      desc: "A digital queue and ticketing system for managing citizen flow and wait times at the LGU office, reducing congestion.",
    },
    {
      id: "chatbot",
      group: "Citizen Services",
      label: "Citizen Chatbot / FAQ Bot",
      cost: 20000,
      desc: "An AI-powered chatbot that answers common citizen questions and guides them through available government services 24/7.",
    },
    {
      id: "docTracking",
      group: "Documents & Records",
      label: "Document Tracking System",
      cost: 20000,
      desc: "Allows citizens and staff to track the processing status of submitted documents or requests at every stage in real time.",
    },
    {
      id: "records",
      group: "Documents & Records",
      label: "Records & File Management",
      cost: 18000,
      desc: "A digital records management system for organizing, searching, and securely retrieving citizen documents and government files.",
    },
    {
      id: "archives",
      group: "Documents & Records",
      label: "Digital Archives System",
      cost: 15000,
      desc: "Long-term digital archiving of government records with secure storage, document indexing, and authorized retrieval capabilities.",
    },
    {
      id: "barangay",
      group: "Documents & Records",
      label: "Barangay Clearance Issuance",
      cost: 12000,
      desc: "Automates the issuance of barangay clearances with online application, staff verification, and official printing workflows.",
    },
    {
      id: "permit",
      group: "Permits & Licensing",
      label: "Business Permit Management",
      cost: 30000,
      desc: "A complete business permit system covering new application, compliance evaluation, approval routing, and annual renewal tracking.",
    },
    {
      id: "clearance",
      group: "Permits & Licensing",
      label: "Police / Barangay Clearance",
      cost: 15000,
      desc: "Manages police or barangay clearance applications with identity verification, approval steps, and digital certificate issuance.",
    },
    {
      id: "buildingPermit",
      group: "Permits & Licensing",
      label: "Building Permit System",
      cost: 25000,
      desc: "Handles building permit applications including document submission, technical review stages, and final approval tracking.",
    },
    {
      id: "licensing",
      group: "Permits & Licensing",
      label: "Professional / Trade Licensing",
      cost: 20000,
      desc: "Manages applications and renewals for professional or trade licenses with compliance tracking and expiry reminders.",
    },
    {
      id: "workflow",
      group: "Workflow & Approvals",
      label: "Approval & Routing Workflow",
      cost: 22000,
      desc: "Configurable multi-step approval routing that automatically forwards requests to the appropriate departments for processing.",
    },
    {
      id: "multiDept",
      group: "Workflow & Approvals",
      label: "Multi-Department User Management",
      cost: 20000,
      desc: "Supports multiple government departments each with their own user accounts, roles, and data access within the same platform.",
    },
    {
      id: "audit",
      group: "Workflow & Approvals",
      label: "Audit Trail & Activity Logs",
      cost: 15000,
      desc: "Full audit trail of every action taken by any user in the system — who did what, when, and from which account.",
    },
    {
      id: "payment",
      group: "Finance",
      label: "Online Payment & Fee Collection",
      cost: 25000,
      desc: "Allows citizens to pay government fees and service charges online through integrated payment gateways at any time.",
    },
    {
      id: "treasury",
      group: "Finance",
      label: "Treasury & Revenue Management",
      cost: 25000,
      desc: "Tracks all revenue collections, official receipts, fund disbursements, and financial position for the local government treasury.",
    },
    {
      id: "budget",
      group: "Finance",
      label: "Budget Planning & Monitoring",
      cost: 20000,
      desc: "Monitors budget allocations per department or program versus actual spending in real time for fiscal accountability.",
    },
    {
      id: "billing",
      group: "Finance",
      label: "Billing & Official Receipts",
      cost: 15000,
      desc: "Generates official billing statements and system-generated receipts for all government-collected fees and transactions.",
    },
    {
      id: "health",
      group: "Community & Social",
      label: "Health Services Module",
      cost: 25000,
      desc: "Manages LGU health services including consultation records, immunization tracking, and community health program monitoring.",
    },
    {
      id: "social",
      group: "Community & Social",
      label: "Social Services / 4Ps Tracking",
      cost: 20000,
      desc: "Tracks social welfare beneficiaries, 4Ps enrollees, and other government aid program recipients with distribution history.",
    },
    {
      id: "blotter",
      group: "Community & Social",
      label: "Blotter & Incident Reports",
      cost: 15000,
      desc: "Digitizes recording and management of blotter entries and incident reports at the barangay or police station level.",
    },
    {
      id: "indigent",
      group: "Community & Social",
      label: "Indigent / Beneficiary Records",
      cost: 15000,
      desc: "Maintains comprehensive records of indigent households and aid beneficiaries for eligibility verification and distribution tracking.",
    },
    {
      id: "projects",
      group: "Infrastructure",
      label: "Infrastructure Projects Tracker",
      cost: 20000,
      desc: "Tracks local government infrastructure projects including current status, budget utilization, timelines, and contractor details.",
    },
    {
      id: "gis",
      group: "Infrastructure",
      label: "GIS / Mapping Integration",
      cost: 35000,
      desc: "Integrates geographic mapping tools to visualize LGU boundaries, infrastructure projects, resources, and citizen data on an interactive map.",
    },
    {
      id: "complaints",
      group: "Infrastructure",
      label: "Citizen Complaints System",
      cost: 12000,
      desc: "A structured citizen complaint submission and tracking system so the LGU can record, assign, and respond to community concerns.",
    },
    {
      id: "sms",
      group: "Communications",
      label: "SMS / Email Notifications",
      cost: 12000,
      desc: "Sends SMS and email notifications to citizens about request status updates, permit approvals, payment confirmations, or government alerts.",
    },
    {
      id: "announcements",
      group: "Communications",
      label: "Public Announcements Portal",
      cost: 8000,
      desc: "A public announcement portal for posting local government news, public advisories, and emergency notices to all residents.",
    },
    {
      id: "hotline",
      group: "Communications",
      label: "Emergency Hotline Integration",
      cost: 10000,
      desc: "Integrates emergency hotline numbers and escalation protocols into the citizen-facing portal for quick access during emergencies.",
    },
    {
      id: "analytics",
      group: "Analytics",
      label: "Analytics & Compliance Reports",
      cost: 20000,
      desc: "Generates compliance reports, service delivery performance metrics, and analytics for department and executive-level review.",
    },
    {
      id: "executive",
      group: "Analytics",
      label: "Executive Dashboard & KPIs",
      cost: 20000,
      desc: "An executive dashboard showing key performance indicators and service metrics across all LGU departments at a single glance.",
    },
    {
      id: "population",
      group: "Analytics",
      label: "Population & Demographics Module",
      cost: 15000,
      desc: "A population registry module for tracking registered residents, household composition, and demographic statistics.",
    },
  ],
  mobile: [],
  custom: [
    {
      id: "auth",
      group: "Foundation",
      label: "User Authentication System",
      cost: 15000,
      desc: "Secure user login with email and password authentication, session token management, and self-service password recovery.",
    },
    {
      id: "roles",
      group: "Foundation",
      label: "Role-Based Access Control",
      cost: 15000,
      desc: "Granular role-based access control defining exactly what each user type can view, add, edit, or delete within the system.",
    },
    {
      id: "dashboard",
      group: "Foundation",
      label: "Custom Admin Dashboard",
      cost: 20000,
      desc: "A fully custom-built admin dashboard with summary widgets, data counters, and metrics relevant to your specific system.",
    },
    {
      id: "darkmode",
      group: "Foundation",
      label: "Dark / Light Mode UI",
      cost: 8000,
      desc: "A toggleable dark and light mode UI theme improving usability and visual comfort across different user environments.",
    },
    {
      id: "crud",
      group: "Data Management",
      label: "Data Entry & CRUD Management",
      cost: 15000,
      desc: "Full create, read, update, and delete data management interfaces for all your core data entities with form validation.",
    },
    {
      id: "import",
      group: "Data Management",
      label: "Data Import / Export (CSV/Excel)",
      cost: 10000,
      desc: "Import bulk data from CSV or Excel files and export records in the same formats for external use or migration.",
    },
    {
      id: "search",
      group: "Data Management",
      label: "Advanced Search & Filtering",
      cost: 10000,
      desc: "Advanced search and filtering across any data module supporting multi-field criteria, date ranges, and instant result display.",
    },
    {
      id: "documents",
      group: "Data Management",
      label: "Document Management",
      cost: 15000,
      desc: "Upload, organize, version, and securely retrieve files and documents attached to records within the system.",
    },
    {
      id: "archive",
      group: "Data Management",
      label: "Archive & Records System",
      cost: 12000,
      desc: "Mark records as archived to remove them from active views while preserving their data integrity and historical accessibility.",
    },
    {
      id: "workflow",
      group: "Workflow",
      label: "Custom Workflow Automation",
      cost: 30000,
      desc: "Fully custom-built workflow automation tailored to your exact business processes, routing, triggers, and approval chains.",
    },
    {
      id: "approval",
      group: "Workflow",
      label: "Multi-Level Approval System",
      cost: 20000,
      desc: "Multi-level approval routing where requests are sequentially passed through defined reviewers before reaching final authorization.",
    },
    {
      id: "tasks",
      group: "Workflow",
      label: "Task & Assignment Management",
      cost: 18000,
      desc: "A task management module for assigning work items, setting deadlines, tracking progress, and sending automated reminders.",
    },
    {
      id: "scheduler",
      group: "Workflow",
      label: "Automated Job Scheduler",
      cost: 15000,
      desc: "Automated background job scheduler for running reports, database cleanups, or notification triggers at set intervals without manual effort.",
    },
    {
      id: "reports",
      group: "Reporting",
      label: "Custom Report Builder",
      cost: 20000,
      desc: "A custom report builder allowing admins to define which data fields to include, apply filters, and export results on demand.",
    },
    {
      id: "charts",
      group: "Reporting",
      label: "Charts & Data Visualization",
      cost: 15000,
      desc: "Interactive data visualization charts — bar, line, pie, and more — providing a visual overview of your system's key data.",
    },
    {
      id: "kpi",
      group: "Reporting",
      label: "KPI Monitoring Dashboard",
      cost: 20000,
      desc: "A live KPI monitoring dashboard with configurable metric cards tailored to your specific business performance goals.",
    },
    {
      id: "pdf",
      group: "Reporting",
      label: "PDF / Excel Report Export",
      cost: 10000,
      desc: "Export any report, record summary, or data table directly as a formatted PDF or Excel file with a single click.",
    },
    {
      id: "email",
      group: "Communications",
      label: "Email Notification System",
      cost: 8000,
      desc: "Automated email notifications sent to relevant users when key actions, status changes, or triggers occur in the system.",
    },
    {
      id: "sms",
      group: "Communications",
      label: "SMS Notification System",
      cost: 10000,
      desc: "Automated SMS alerts that notify users or clients of important events, approvals, or updates in real time.",
    },
    {
      id: "chat",
      group: "Communications",
      label: "Internal Messaging / Chat",
      cost: 20000,
      desc: "An internal messaging and group chat module for team communication and collaboration directly within the system.",
    },
    {
      id: "api",
      group: "Integrations",
      label: "REST API / System Integration",
      cost: 25000,
      desc: "Connects your system to third-party applications or existing internal software through a secure REST API integration layer.",
    },
    {
      id: "payment",
      group: "Integrations",
      label: "Payment Gateway Integration",
      cost: 22000,
      desc: "Integrates a payment gateway to accept online payments for goods, services, or subscriptions directly within the system.",
    },
    {
      id: "maps",
      group: "Integrations",
      label: "Maps / Geolocation Integration",
      cost: 15000,
      desc: "Embeds mapping and geolocation features for tracking assets, address lookup, or location-based functionality.",
    },
    {
      id: "biometric",
      group: "Integrations",
      label: "Biometric / RFID Integration",
      cost: 25000,
      desc: "Connects biometric fingerprint scanners or RFID readers to the system for identity verification or automated attendance tracking.",
    },
    {
      id: "ai",
      group: "Advanced",
      label: "AI / Machine Learning Features",
      cost: 60000,
      desc: "Incorporates AI or machine learning capabilities such as predictions, intelligent classifications, or automated decision support.",
    },
    {
      id: "realtime",
      group: "Advanced",
      label: "Real-Time Updates (WebSocket)",
      cost: 25000,
      desc: "Live data updates using WebSocket technology — changes made anywhere in the system appear instantly for all active users.",
    },
    {
      id: "mobile",
      group: "Advanced",
      label: "Mobile Companion App",
      cost: 80000,
      desc: "A companion mobile application for Android and/or iOS that mirrors or extends core system functionality for on-the-go use.",
    },
    {
      id: "multitenancy",
      group: "Advanced",
      label: "Multi-Tenant Architecture",
      cost: 40000,
      desc: "A multi-tenant architecture allowing the system to serve multiple independent clients or organizations from one shared codebase.",
    },
    {
      id: "audit",
      group: "Security",
      label: "Audit Logs & Activity Monitoring",
      cost: 15000,
      desc: "Detailed audit logs recording every user action — what data was changed, by whom, from which IP address, and at what time.",
    },
    {
      id: "encryption",
      group: "Security",
      label: "Data Encryption & Security",
      cost: 15000,
      desc: "Implements data encryption at rest and in transit to protect sensitive information from unauthorized access or breaches.",
    },
    {
      id: "twofa",
      group: "Security",
      label: "Two-Factor Authentication",
      cost: 10000,
      desc: "Two-factor authentication requiring users to verify identity with a code sent to their email or authenticator app at login.",
    },
    {
      id: "backup",
      group: "Security",
      label: "Automated Backup System",
      cost: 8000,
      desc: "Sets up automated system backups on a configured schedule to protect all data from loss, corruption, or accidental deletion.",
    },
  ],
};

// ─── SCALE ────────────────────────────────────────────────────────────────────
const SCALES = [
  { id: "small", label: "Small", sub: "1–10 users", add: 0, color: "#3b82f6" },
  {
    id: "medium",
    label: "Medium",
    sub: "10–100 users",
    add: 50000,
    color: "#06b6d4",
  },
  {
    id: "large",
    label: "Large",
    sub: "100–1000 users",
    add: 150000,
    color: "#8b5cf6",
  },
  {
    id: "enterprise",
    label: "Enterprise",
    sub: "1000+ users",
    add: 400000,
    color: "#f59e0b",
  },
];

// ─── SCALE EXPLAINER DATA ─────────────────────────────────────────────────────
const SCALE_POINTS = [
  {
    icon: "🗄️",
    title: "Database Architecture",
    desc: "More users means more complex database design — connection pooling, query optimization, and possibly read replicas to prevent slowdowns.",
  },
  {
    icon: "🔐",
    title: "Session & Auth Management",
    desc: "Each logged-in user holds an active session. Higher user counts require more robust token handling, security layers, and session storage.",
  },
  {
    icon: "⚡",
    title: "Performance Optimization",
    desc: "Systems built for scale need caching strategies (e.g. Redis), optimized queries, and background job queues that simpler builds skip entirely.",
  },
  {
    icon: "🖥️",
    title: "Infrastructure Sizing",
    desc: "We size the recommended server setup to match your user load — small teams can run on basic VPS, while enterprise needs load balancers and auto-scaling.",
  },
];

// ─── TIMELINE ─────────────────────────────────────────────────────────────────
const TIMELINES = [
  {
    id: "flexible",
    label: "Flexible",
    sub: "Standard schedule",
    weeks: "8–16 weeks",
    add: 0,
    badge: null,
  },
  {
    id: "priority",
    label: "Priority",
    sub: "Faster turnaround",
    weeks: "4–8 weeks",
    add: 25000,
    badge: "Rush Fee",
  },
  {
    id: "urgent",
    label: "Urgent",
    sub: "Expedited delivery",
    weeks: "2–4 weeks",
    add: 60000,
    badge: "Rush Fee",
  },
];

// ─── TIMELINE EXPLAINER DATA ──────────────────────────────────────────────────
const TIMELINE_POINTS = [
  {
    icon: "👥",
    title: "Additional Developer Hours",
    desc: "Rush projects require more developers working in parallel or extended hours to compress the delivery window safely.",
  },
  {
    icon: "🔍",
    title: "Compressed QA & Testing",
    desc: "Faster delivery still requires thorough testing. We allocate dedicated QA time to maintain quality even under tight deadlines.",
  },
  {
    icon: "📋",
    title: "Priority Queue Placement",
    desc: "Your project moves to the front of our active work queue, which means other projects are re-scheduled to accommodate your timeline.",
  },
];

// ─── ADDONS ───────────────────────────────────────────────────────────────────
const ADDONS = [
  {
    id: "hosting",
    label: "VPS / Cloud Hosting Setup",
    cost: 10000,
    desc: "Full setup of a VPS or cloud server (e.g., DigitalOcean, AWS, Hostinger) including OS configuration, firewall, and deployment.",
  },
  {
    id: "ssl",
    label: "SSL Configuration",
    cost: 3000,
    desc: "Installs and configures an SSL certificate on your server to enable HTTPS and secure all data transmissions.",
  },
  {
    id: "domain",
    label: "Domain Setup Assistance",
    cost: 2000,
    desc: "Assists in registering your domain name and configuring DNS records to point correctly to your hosting server.",
  },
  {
    id: "backup",
    label: "Backup & Disaster Recovery Setup",
    cost: 8000,
    desc: "Sets up a disaster recovery plan with automated backups stored in a separate location for fast restoration in case of failure.",
  },
  {
    id: "training",
    label: "Admin Training & Orientation",
    cost: 5000,
    desc: "A hands-on orientation session for your admin team covering how to use and manage the system features effectively.",
  },
  {
    id: "docs",
    label: "Technical Documentation",
    cost: 10000,
    desc: "Comprehensive technical documentation including system architecture, admin user guide, and developer handover notes.",
  },
  {
    id: "warranty",
    label: "3-Month Post-Launch Warranty Support",
    cost: 15000,
    desc: "Priority bug-fix support for three months after launch — any issues found are resolved at no additional charge.",
  },
  {
    id: "support1yr",
    label: "1-Year Priority Support Plan",
    cost: 60000,
    desc: "Full one-year priority support plan covering bug fixes, minor enhancements, and regular maintenance checks.",
  },
];

const STEPS = ["Project Type", "Features", "Scale & Add-ons", "Your Quote"];

// ─── PDF HELPERS ──────────────────────────────────────────────────────────────
async function loadLogoDataUrl(src) {
  try {
    const res = await fetch(src);
    if (!res.ok) return null;
    const blob = await res.blob();
    return await new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
  } catch {
    return null;
  }
}

// ─── PDF EXPORT ───────────────────────────────────────────────────────────────
async function generatePDF({
  project,
  features,
  selectedFeatures,
  scale,
  timeline,
  selectedAddons,
  ADDONS,
  basePrice,
  featuresTotal,
  scaleAdd,
  timelineAdd,
  addonsTotal,
  grandTotal,
}) {
  const logoDataUrl = await loadLogoDataUrl("/PDF_Logo.png");
  const doc = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const pageW = doc.internal.pageSize.getWidth();
  const pageH = doc.internal.pageSize.getHeight();
  const ML = 18,
    MR = 18;
  const CW = pageW - ML - MR;
  let y = 0;

  const BLUE = [30, 86, 210];
  const DARK = [20, 28, 48];
  const GRAY = [90, 100, 120];
  const LGRAY = [245, 246, 250];
  const BORDER = [220, 224, 234];
  const WHITE = [255, 255, 255];
  const GREEN = [22, 163, 74];
  const TEAL = [13, 148, 136];

  const checkPage = (needed) => {
    if (y + needed > pageH - 18) {
      doc.addPage();
      y = 20;
    }
  };

  const sectionTitle = (label) => {
    checkPage(16);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(8.5);
    doc.setTextColor(...BLUE);
    doc.text(label.toUpperCase(), ML, y + 4);
    doc.setDrawColor(...BLUE);
    doc.setLineWidth(0.4);
    doc.line(ML, y + 6, pageW - MR, y + 6);
    y += 11;
  };

  const tableDefaults = {
    margin: { left: ML, right: MR },
    styles: {
      font: "helvetica",
      fontSize: 8.5,
      cellPadding: { top: 3.5, bottom: 3.5, left: 5, right: 5 },
      textColor: DARK,
      lineColor: BORDER,
      lineWidth: 0.2,
      overflow: "linebreak",
    },
    headStyles: {
      fillColor: BLUE,
      textColor: WHITE,
      fontStyle: "bold",
      fontSize: 8,
    },
    alternateRowStyles: { fillColor: LGRAY },
  };

  const HDR_H = 65;
  doc.setFillColor(248, 249, 252);
  doc.rect(0, 0, pageW, HDR_H, "F");

  if (logoDataUrl) {
    const logoW = 120;
    const logoH = logoW / 2;
    const logoY = 0;
    doc.addImage(logoDataUrl, "JPEG", -21, logoY, logoW, logoH);
  } else {
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.setTextColor(...BLUE);
    doc.text("MDS", ML, 22);
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text("Software Development Services", ML, 29);
  }

  doc.setFont("helvetica", "bold");
  doc.setFontSize(20);
  doc.setTextColor(...DARK);
  doc.text("PROJECT QUOTE", pageW - MR, 17, { align: "right" });

  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  const dateStr = new Date().toLocaleDateString("en-PH", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });
  const refNum = `MDS-${Date.now().toString().slice(-8)}`;
  doc.text(`Date: ${dateStr}`, pageW - MR, 25, { align: "right" });
  doc.text(`Ref: ${refNum}`, pageW - MR, 31, { align: "right" });
  doc.text("mdstechservices.info@gmail.com", pageW - MR, 37, {
    align: "right",
  });
  doc.text("mdstechservices-ph.online", pageW - MR, 43, { align: "right" });

  doc.setFillColor(...BLUE);
  doc.rect(0, HDR_H, pageW, 1.5, "F");
  y = HDR_H + 10;

  doc.setFillColor(232, 240, 254);
  doc.roundedRect(ML, y, CW, 28, 3, 3, "F");
  doc.setDrawColor(...BLUE);
  doc.setLineWidth(0.4);
  doc.roundedRect(ML, y, CW, 28, 3, 3, "S");
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...BLUE);
  doc.text("ESTIMATED PROJECT TOTAL", pageW / 2, y + 7, { align: "center" });
  doc.setFont("helvetica", "bold");
  doc.setFontSize(24);
  doc.setTextColor(...DARK);
  doc.text(php(grandTotal), pageW / 2, y + 18, { align: "center" });
  doc.setFont("helvetica", "normal");
  doc.setFontSize(7.5);
  doc.setTextColor(...GRAY);
  doc.text(
    `50% downpayment to start: ${php(grandTotal / 2)}   -   Balance upon completion: ${php(grandTotal / 2)}`,
    pageW / 2,
    y + 24.5,
    { align: "center" },
  );
  y += 36;

  sectionTitle("Project Overview");
  autoTable(doc, {
    ...tableDefaults,
    startY: y,
    head: [["Field", "Detail", "Cost"]],
    body: [
      ["Project Type", stripEmoji(project.label), php(project.base)],
      [
        "User Scale",
        `${scale.label} — ${scale.sub}`,
        scale.add === 0 ? "Included" : `+ ${php(scale.add)}`,
      ],
      [
        "Timeline",
        `${timeline.label} (${timeline.weeks}) — ${timeline.sub}`,
        timeline.add === 0 ? "Included" : `+ ${php(timeline.add)}`,
      ],
    ],
    columnStyles: {
      0: { cellWidth: 38, fontStyle: "bold", textColor: GRAY, fontSize: 7.5 },
      1: { cellWidth: "auto" },
      2: { cellWidth: 42, halign: "right", fontStyle: "bold" },
    },
    didParseCell(data) {
      if (data.section === "body" && data.column.index === 2)
        data.cell.styles.textColor =
          data.cell.raw === "Included" ? GREEN : BLUE;
    },
  });
  y = doc.lastAutoTable.finalY + 10;

  if (project.includes?.length > 0) {
    sectionTitle(`Base Package - Included in ${php(basePrice)}`);
    autoTable(doc, {
      ...tableDefaults,
      startY: y,
      head: [["Included in base price"]],
      body: project.includes.map((item) => [item]),
      headStyles: {
        fillColor: TEAL,
        textColor: WHITE,
        fontStyle: "bold",
        fontSize: 8,
      },
      alternateRowStyles: { fillColor: [240, 253, 250] },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  const featureRows = features
    .filter((f) => selectedFeatures.includes(f.id) && f.cost > 0)
    .map((f) => [
      stripEmoji(f.group),
      stripEmoji(f.label),
      f.desc || "-",
      `+ ${php(f.cost)}`,
    ]);
  if (featureRows.length > 0) {
    checkPage(80);
    sectionTitle("Selected Features");
    autoTable(doc, {
      ...tableDefaults,
      startY: y,
      head: [["Category", "Feature", "Description", "Cost"]],
      body: featureRows,
      columnStyles: {
        0: { cellWidth: 30, fontStyle: "bold", textColor: GRAY, fontSize: 7 },
        1: { cellWidth: 42, fontStyle: "bold" },
        2: { cellWidth: "auto", textColor: GRAY },
        3: {
          cellWidth: 30,
          halign: "right",
          fontStyle: "bold",
          textColor: BLUE,
        },
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  const addonRows = ADDONS.filter((a) => selectedAddons.includes(a.id)).map(
    (a) => [stripEmoji(a.label), a.desc || "-", `+ ${php(a.cost)}`],
  );
  if (addonRows.length > 0) {
    sectionTitle("Optional Add-ons");
    autoTable(doc, {
      ...tableDefaults,
      startY: y,
      head: [["Add-On", "Description", "Cost"]],
      body: addonRows,
      columnStyles: {
        0: { cellWidth: 52, fontStyle: "bold" },
        1: { cellWidth: "auto", textColor: GRAY },
        2: {
          cellWidth: 30,
          halign: "right",
          fontStyle: "bold",
          textColor: BLUE,
        },
      },
    });
    y = doc.lastAutoTable.finalY + 10;
  }

  checkPage(80);
  sectionTitle("Price Breakdown");
  autoTable(doc, {
    ...tableDefaults,
    startY: y,
    head: [["Component", "Amount"]],
    body: [
      ["Base Package", php(basePrice)],
      [
        "Selected Features",
        featuresTotal > 0 ? `+ ${php(featuresTotal)}` : "PHP 0",
      ],
      ["User Scale", scaleAdd > 0 ? `+ ${php(scaleAdd)}` : "Included"],
      ["Timeline", timelineAdd > 0 ? `+ ${php(timelineAdd)}` : "Included"],
      ["Add-ons", addonsTotal > 0 ? `+ ${php(addonsTotal)}` : "None"],
    ],
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 52, halign: "right", fontStyle: "bold" },
    },
    didParseCell(data) {
      if (data.section === "body" && data.column.index === 1) {
        const v = data.cell.raw;
        if (v === "Included" || v === "None")
          data.cell.styles.textColor = GREEN;
        else if (v.startsWith("+")) data.cell.styles.textColor = BLUE;
      }
    },
  });
  y = doc.lastAutoTable.finalY;

  autoTable(doc, {
    startY: y,
    margin: { left: ML, right: MR },
    body: [["GRAND TOTAL", php(grandTotal)]],
    styles: {
      font: "helvetica",
      fontStyle: "bold",
      fontSize: 12,
      cellPadding: { top: 5, bottom: 5, left: 5, right: 5 },
      fillColor: DARK,
      textColor: WHITE,
    },
    columnStyles: {
      0: { cellWidth: "auto" },
      1: { cellWidth: 52, halign: "right", textColor: [100, 180, 255] },
    },
  });
  y = doc.lastAutoTable.finalY + 10;

  checkPage(30);
  const halfW = (CW - 6) / 2;
  doc.setFillColor(...BLUE);
  doc.roundedRect(ML, y, halfW, 18, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(180, 210, 255);
  doc.text("50% DOWNPAYMENT TO START", ML + halfW / 2, y + 6.5, {
    align: "center",
  });
  doc.setFontSize(12);
  doc.setTextColor(...WHITE);
  doc.text(php(grandTotal / 2), ML + halfW / 2, y + 13.5, { align: "center" });

  const rx = ML + halfW + 6;
  doc.setFillColor(...TEAL);
  doc.roundedRect(rx, y, halfW, 18, 2, 2, "F");
  doc.setFont("helvetica", "bold");
  doc.setFontSize(7);
  doc.setTextColor(180, 240, 220);
  doc.text("BALANCE UPON COMPLETION", rx + halfW / 2, y + 6.5, {
    align: "center",
  });
  doc.setFontSize(12);
  doc.setTextColor(...WHITE);
  doc.text(php(grandTotal / 2), rx + halfW / 2, y + 13.5, { align: "center" });
  y += 26;

  sectionTitle("Terms & Notes");
  const notes = [
    "Prices are exact based on your selections. Final confirmation happens after a free consultation.",
    "Domain registration and web hosting fees are not included and are paid directly by the client.",
    "50% downpayment is required to start the project. Remaining 50% is due upon completion and turnover.",
    "Source code and all project files are fully turned over upon complete payment.",
    "Changes to scope or features after project kick-off may require additional costing.",
    "This quote is valid for 30 days from the date of generation.",
  ];
  notes.forEach((note, i) => {
    const lines = doc.splitTextToSize(`${i + 1}.  ${note}`, CW - 4);
    const rowH = lines.length * 4.8 + 3;
    checkPage(rowH);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.setTextColor(...GRAY);
    doc.text(lines, ML + 2, y + 3.5);
    y += rowH;
  });

  const totalPages = doc.internal.getNumberOfPages();
  for (let p = 1; p <= totalPages; p++) {
    doc.setPage(p);
    doc.setDrawColor(...BORDER);
    doc.setLineWidth(0.3);
    doc.line(ML, pageH - 12, pageW - MR, pageH - 12);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(7);
    doc.setTextColor(...GRAY);
    doc.text(
      "MDS Software Development Services  -  mdstechservices.info@gmail.com  -  mdstechservices-ph.online",
      ML,
      pageH - 7,
    );
    doc.setTextColor(...BLUE);
    doc.setFont("helvetica", "bold");
    doc.text(`Page ${p} of ${totalPages}`, pageW - MR, pageH - 7, {
      align: "right",
    });
  }

  const safeLabel = stripEmoji(project.label).replace(/[^a-zA-Z0-9]/g, "_");
  doc.save(`MDS_Quote_${safeLabel}_${Date.now()}.pdf`);
}

// ─── COMPONENT ────────────────────────────────────────────────────────────────
export default function Packages() {
  const [step, setStep] = useState(0);
  const [projectId, setProjectId] = useState(null);
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [scaleId, setScaleId] = useState("small");
  const [timelineId, setTimelineId] = useState("flexible");
  const [selectedAddons, setSelectedAddons] = useState([]);
  const [expandedCard, setExpandedCard] = useState(null);
  const [form, setForm] = useState({
    name: "",
    email: "",
    company: "",
    message: "",
  });
  const [status, setStatus] = useState("");
  const [loading, setLoading] = useState(false);
  const [quoteIncludesOpen, setQuoteIncludesOpen] = useState(false);
  const [pdfExported, setPdfExported] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [scaleExplainerOpen, setScaleExplainerOpen] = useState(false);
  const [timelineExplainerOpen, setTimelineExplainerOpen] = useState(false);

  const cardPanelRef = useRef(null);
  const stepperRef = useRef(null);

  const project = PROJECT_TYPES.find((p) => p.id === projectId);
  const features = projectId ? FEATURES[projectId] : [];
  const scale = SCALES.find((s) => s.id === scaleId) || SCALES[0];
  const timeline = TIMELINES.find((t) => t.id === timelineId) || TIMELINES[0];

  useEffect(() => {
    setSelectedFeatures(getIncludedFeatureIds(projectId));
  }, [projectId]);

  useEffect(() => {
    if (step === 0) return;
    if (stepperRef.current) {
      stepperRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [step]);

  useEffect(() => {
    setExpandedCard(null);
  }, [step]);

  useEffect(() => {
    if (step < 3) setPdfExported(false);
  }, [step]);

  useEffect(() => {
    if (step !== 3) setStatus("");
  }, [step]);

  useEffect(() => {
    if (step === 3) setQuoteIncludesOpen(false);
  }, [step]);

  useEffect(() => {
    if (!expandedCard) return;
    const handler = (e) => {
      if (cardPanelRef.current && !cardPanelRef.current.contains(e.target))
        setExpandedCard(null);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [expandedCard]);

  useEffect(() => {
    if (status === "success" && stepperRef.current) {
      stepperRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, [status]);

  const basePrice = project?.base || 0;
  const featuresTotal = features
    .filter((f) => selectedFeatures.includes(f.id) && f.cost > 0)
    .reduce((s, f) => s + f.cost, 0);
  const addonsTotal = ADDONS.filter((a) =>
    selectedAddons.includes(a.id),
  ).reduce((s, a) => s + a.cost, 0);
  const scaleAdd = scale.add;
  const timelineAdd = timeline.add;
  const grandTotal =
    basePrice + featuresTotal + scaleAdd + timelineAdd + addonsTotal;

  // ── FIX: toggleFeature now respects MUTEX_GROUPS so that mutually-exclusive
  //         features (e.g. website page tiers) behave like radio buttons —
  //         selecting one automatically deselects the others in the same group.
  const toggleFeature = (id) => {
    const feature = features.find((f) => f.id === id);
    if (!feature || feature.cost === 0) return;

    const mutexGroup = (MUTEX_GROUPS[projectId] || []).find((g) =>
      g.includes(id),
    );

    setSelectedFeatures((prev) => {
      // Strip any sibling in the same mutex group before toggling
      const withoutMutex = mutexGroup
        ? prev.filter((x) => !mutexGroup.includes(x))
        : prev;

      // If the tapped item was already the sole selected member, deselect it;
      // otherwise select it (the siblings were already stripped above).
      return prev.includes(id)
        ? withoutMutex.filter((x) => x !== id)
        : [...withoutMutex, id];
    });
  };

  const toggleAddon = (id) =>
    setSelectedAddons((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );

  const handleExportPDF = () => {
    setPdfGenerating(true);
    generatePDF({
      project,
      features,
      selectedFeatures,
      scale,
      timeline,
      selectedAddons,
      ADDONS,
      basePrice,
      featuresTotal,
      scaleAdd,
      timelineAdd,
      addonsTotal,
      grandTotal,
    })
      .then(() => setPdfExported(true))
      .catch((e) => console.error("PDF error:", e))
      .finally(() => setPdfGenerating(false));
  };

  // ─── SUBMIT ───────────────────────────────────────────────────────────────
  const handleSubmit = async () => {
    const trimmedName = form.name.trim();
    const trimmedEmail = form.email.trim();
    const trimmedCompany = form.company.trim();
    const trimmedMessage = form.message.trim();

    if (!trimmedName) {
      setStatus("missing");
      return;
    }
    if (!trimmedEmail || !isValidEmail(trimmedEmail)) {
      setStatus("invalidemail");
      return;
    }

    setLoading(true);
    setStatus("");

    const time = new Date().toLocaleString("en-PH", {
      timeZone: "Asia/Manila",
      dateStyle: "medium",
      timeStyle: "short",
    });

    const featureRows = features
      .filter((f) => selectedFeatures.includes(f.id) && f.cost > 0)
      .map(
        (f, i) => `
    <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f8f9fc"};">
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#555;font-weight:600;">${stripEmoji(f.group)}</td>
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#1a1a2e;font-weight:600;">${stripEmoji(f.label)}</td>
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#444;line-height:1.5;">${f.desc || ""}</td>
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#2563eb;text-align:right;font-weight:700;white-space:nowrap;">+ ${php(f.cost)}</td>
    </tr>`,
      )
      .join("");

    const addonRows = ADDONS.filter((a) => selectedAddons.includes(a.id))
      .map(
        (a, i) => `
    <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f8f9fc"};">
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#1a1a2e;font-weight:600;">${stripEmoji(a.label)}</td>
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#444;line-height:1.5;">${a.desc || ""}</td>
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#2563eb;text-align:right;font-weight:700;white-space:nowrap;">+ ${php(a.cost)}</td>
    </tr>`,
      )
      .join("");

    const baseIncludesRows = (project?.includes || [])
      .map(
        (item, i) => `
    <tr style="background:${i % 2 === 0 ? "#ffffff" : "#f0fdf8"};">
      <td style="padding:9px 12px;border:1px solid #e0e4ea;font-size:12px;color:#1a1a2e;">${item}</td>
    </tr>`,
      )
      .join("");

    const msg = `
<div style="font-family:Arial,sans-serif;max-width:680px;margin:0 auto;background:#f4f6fb;padding:0;">

  <!-- HEADER -->
  <div style="background:#ffffff;padding:28px 36px 20px;border-bottom:3px solid #2563eb;">
    <table style="width:100%;border-collapse:collapse;">
      <tr>
        <td style="vertical-align:top;">
          <div style="font-size:13px;font-weight:700;color:#1a1a2e;">MDS Software Development Services</div>
          <div style="font-size:11px;color:#888;margin-top:3px;">mdstechservices.info@gmail.com</div>
          <div style="font-size:11px;color:#888;">mdstechservices-ph.online</div>
        </td>
        <td style="text-align:right;vertical-align:top;">
          <div style="font-size:26px;font-weight:900;color:#1a1a2e;letter-spacing:-0.5px;">PROJECT QUOTE</div>
          <div style="font-size:11px;color:#888;margin-top:4px;">Date: ${new Date().toLocaleDateString("en-PH", { year: "numeric", month: "long", day: "numeric" })}</div>
          <div style="font-size:11px;color:#888;">Ref: MDS-${Date.now().toString().slice(-8)}</div>
        </td>
      </tr>
    </table>
  </div>

  <!-- ESTIMATED TOTAL BANNER -->
  <div style="background:#ffffff;padding:28px 36px;">
    <div style="border:1.5px solid #2563eb;border-radius:8px;padding:22px;text-align:center;background:#f0f4ff;">
      <div style="font-size:11px;color:#2563eb;letter-spacing:2px;text-transform:uppercase;margin-bottom:8px;">Estimated Project Total</div>
      <div style="font-size:36px;font-weight:900;color:#1a1a2e;letter-spacing:-1px;">${php(grandTotal)}</div>
      <div style="font-size:12px;color:#666;margin-top:8px;">
        50% downpayment to start: <strong style="color:#2563eb;">${php(grandTotal / 2)}</strong>
        &nbsp; - &nbsp;
        Balance upon completion: <strong style="color:#2563eb;">${php(grandTotal / 2)}</strong>
      </div>
    </div>
  </div>

  <!-- PROJECT OVERVIEW -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <div style="font-size:12px;font-weight:800;color:#2563eb;letter-spacing:1px;text-transform:uppercase;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #2563eb;">Project Overview</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <tr>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #1d4ed8;">Field</th>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #1d4ed8;">Detail</th>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:right;font-weight:700;border:1px solid #1d4ed8;">Cost</th>
      </tr>
      <tr style="background:#f8f9fc;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#555;font-weight:600;">Project Type</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">${stripEmoji(project?.label)}</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#2563eb;text-align:right;font-weight:700;">${php(basePrice)}</td>
      </tr>
      <tr style="background:#ffffff;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#555;font-weight:600;">User Scale</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">${scale.label} — ${scale.sub}</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:${scale.add === 0 ? "#16a34a" : "#2563eb"};">${scale.add === 0 ? "Included" : "+ " + php(scale.add)}</td>
      </tr>
      <tr style="background:#f8f9fc;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#555;font-weight:600;">Timeline</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">${timeline.label} (${timeline.weeks}) — ${timeline.sub}</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:${timeline.add === 0 ? "#16a34a" : "#2563eb"};">${timeline.add === 0 ? "Included" : "+ " + php(timeline.add)}</td>
      </tr>
    </table>
  </div>

  ${
    baseIncludesRows
      ? `
  <!-- BASE PACKAGE -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <div style="font-size:12px;font-weight:800;color:#0d9488;letter-spacing:1px;text-transform:uppercase;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #0d9488;">Base Package — Included in ${php(basePrice)}</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <tr>
        <th style="background:#0d9488;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #0f766e;">Included in Base Price</th>
      </tr>
      ${baseIncludesRows}
    </table>
  </div>`
      : ""
  }

  ${
    featureRows
      ? `
  <!-- SELECTED FEATURES -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <div style="font-size:12px;font-weight:800;color:#2563eb;letter-spacing:1px;text-transform:uppercase;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #2563eb;">Selected Features</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <tr>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #1d4ed8;width:16%;">Category</th>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #1d4ed8;width:20%;">Feature</th>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #1d4ed8;">Description</th>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:right;font-weight:700;border:1px solid #1d4ed8;width:14%;">Cost</th>
      </tr>
      ${featureRows}
    </table>
  </div>`
      : ""
  }

  ${
    addonRows
      ? `
  <!-- ADD-ONS -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <div style="font-size:12px;font-weight:800;color:#0891b2;letter-spacing:1px;text-transform:uppercase;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #0891b2;">Optional Add-Ons</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <tr>
        <th style="background:#0891b2;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #0e7490;width:28%;">Add-On</th>
        <th style="background:#0891b2;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #0e7490;">Description</th>
        <th style="background:#0891b2;color:#fff;padding:9px 12px;text-align:right;font-weight:700;border:1px solid #0e7490;width:14%;">Cost</th>
      </tr>
      ${addonRows}
    </table>
  </div>`
      : ""
  }

  <!-- PRICE BREAKDOWN -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <div style="font-size:12px;font-weight:800;color:#2563eb;letter-spacing:1px;text-transform:uppercase;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #2563eb;">Price Breakdown</div>
    <table style="width:100%;border-collapse:collapse;font-size:12px;">
      <tr>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:left;font-weight:700;border:1px solid #1d4ed8;">Component</th>
        <th style="background:#2563eb;color:#fff;padding:9px 12px;text-align:right;font-weight:700;border:1px solid #1d4ed8;">Amount</th>
      </tr>
      <tr style="background:#f8f9fc;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">Base Package</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:#1a1a2e;">${php(basePrice)}</td>
      </tr>
      <tr style="background:#ffffff;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">Selected Features</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:#2563eb;">${featuresTotal > 0 ? "+ " + php(featuresTotal) : "PHP 0"}</td>
      </tr>
      <tr style="background:#f8f9fc;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">User Scale</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:${scaleAdd > 0 ? "#2563eb" : "#16a34a"};">${scaleAdd > 0 ? "+ " + php(scaleAdd) : "Included"}</td>
      </tr>
      <tr style="background:#ffffff;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">Timeline</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:${timelineAdd > 0 ? "#2563eb" : "#16a34a"};">${timelineAdd > 0 ? "+ " + php(timelineAdd) : "Included"}</td>
      </tr>
      <tr style="background:#f8f9fc;">
        <td style="padding:9px 12px;border:1px solid #e0e4ea;color:#1a1a2e;">Add-Ons</td>
        <td style="padding:9px 12px;border:1px solid #e0e4ea;text-align:right;font-weight:700;color:${addonsTotal > 0 ? "#2563eb" : "#999"};">${addonsTotal > 0 ? "+ " + php(addonsTotal) : "None"}</td>
      </tr>
      <tr>
        <td style="padding:13px 12px;background:#1a1a2e;color:#ffffff;font-size:14px;font-weight:800;">GRAND TOTAL</td>
        <td style="padding:13px 12px;background:#1a1a2e;color:#64b0ff;font-size:14px;font-weight:800;text-align:right;">${php(grandTotal)}</td>
      </tr>
    </table>
  </div>

  <!-- PAYMENT SPLIT -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <table style="width:100%;border-collapse:collapse;">
      <tr>
        <td style="width:50%;padding-right:8px;">
          <div style="background:#2563eb;border-radius:8px;padding:16px;text-align:center;">
            <div style="font-size:10px;color:rgba(255,255,255,0.7);letter-spacing:1px;text-transform:uppercase;margin-bottom:6px;">50% Downpayment to Start</div>
            <div style="font-size:20px;font-weight:900;color:#ffffff;">${php(grandTotal / 2)}</div>
          </div>
        </td>
        <td style="width:50%;padding-left:8px;">
          <div style="background:#0d9488;border-radius:8px;padding:16px;text-align:center;">
            <div style="font-size:10px;color:rgba(255,255,255,0.7);letter-spacing:1px;text-transform:uppercase;margin-bottom:6px;">Balance upon Completion</div>
            <div style="font-size:20px;font-weight:900;color:#ffffff;">${php(grandTotal / 2)}</div>
          </div>
        </td>
      </tr>
    </table>
  </div>

  <!-- TERMS -->
  <div style="background:#ffffff;padding:0 36px 24px;">
    <div style="font-size:12px;font-weight:800;color:#2563eb;letter-spacing:1px;text-transform:uppercase;margin-bottom:10px;padding-bottom:6px;border-bottom:2px solid #2563eb;">Terms &amp; Notes</div>
    ${[
      "Prices are exact based on your selections. Final confirmation happens after a free consultation.",
      "Domain registration and web hosting fees are not included and are paid directly by the client.",
      "50% downpayment is required to start the project. Remaining 50% is due upon completion and turnover.",
      "Source code and all project files are fully turned over upon complete payment.",
      "Changes to scope or features after project kick-off may require additional costing.",
      "This quote is valid for 30 days from the date of generation.",
    ]
      .map(
        (note, i) => `
      <div style="display:flex;gap:10px;margin-bottom:8px;font-size:12px;color:#555;line-height:1.6;">
        <span style="font-weight:700;color:#2563eb;flex-shrink:0;">${i + 1}.</span>
        <span>${note}</span>
      </div>`,
      )
      .join("")}
  </div>

  <!-- FOOTER -->
  <div style="background:#1a1a2e;border-radius:0 0 8px 8px;padding:16px 36px;text-align:center;">
    <div style="font-size:12px;color:rgba(255,255,255,0.5);">MDS Software Development Services</div>
    <div style="font-size:11px;color:rgba(255,255,255,0.35);margin-top:3px;">mdstechservices.info@gmail.com &nbsp;·&nbsp; mdstechservices-ph.online</div>
  </div>

</div>`;

    try {
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        {
          to_email: "mdstechservices.info@gmail.com",
          from_name: trimmedCompany ? `${trimmedName} (${trimmedCompany})` : trimmedName,
          reply_to: trimmedEmail,
          name: trimmedName,
          email: trimmedEmail,
          company: trimmedCompany || "Not provided",
          title: `Quote Request: ${stripEmoji(project?.label).replace(/\//g, "-")}`,
          message: msg,
          notes: trimmedMessage ? `Note from client: ${trimmedMessage}` : "",
          time,
        },
        EMAILJS_PUBLIC_KEY,
      );
    } catch {
      setStatus("error");
      setLoading(false);
      return;
    }

    const featureList = features
      .filter((f) => selectedFeatures.includes(f.id))
      .map((f) => f.label)
      .join(", ");
    const addonList = ADDONS.filter((a) => selectedAddons.includes(a.id))
      .map((a) => a.label)
      .join(", ");

    try {
      await addDoc(collection(db, "package_inquiries"), {
        name: trimmedName,
        email: trimmedEmail,
        company: trimmedCompany,
        message: trimmedMessage,
        projectType: project?.label,
        features: featureList,
        scale: scale.label,
        timeline: `${timeline.label} (${timeline.weeks})`,
        addons: addonList,
        grandTotal,
        pdfExported,
        createdAt: serverTimestamp(),
      });
    } catch (e) {
      console.warn("Firestore:", e);
    }

    setStatus("success");
    setForm({ name: "", email: "", company: "", message: "" });
    setLoading(false);
  };

  return (
    <PageTransition>
      <div
        style={{
          background: "#040610",
          minHeight: "100vh",
          color: "#fff",
          overflowX: "hidden",
        }}
      >
        <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=JetBrains+Mono:wght@400;500&display=swap');
          *, *::before, *::after { box-sizing:border-box; }
          :root { --bg:#040610; --bg2:#070b1a; --border:rgba(255,255,255,0.07); --muted:rgba(255,255,255,0.45); --blue:#2563eb; --cyan:#06b6d4; }

          @keyframes fadeUp   { from{opacity:0;transform:translateY(20px)} to{opacity:1;transform:translateY(0)} }
          @keyframes blink    { 0%,100%{opacity:1} 50%{opacity:0.2} }
          @keyframes glow     { 0%,100%{opacity:.5;transform:translateX(-50%) scale(1)} 50%{opacity:.9;transform:translateX(-50%) scale(1.06)} }
          @keyframes gradShift{ 0%,100%{background-position:0% 50%} 50%{background-position:100% 50%} }
          @keyframes slideIn  { from{opacity:0;transform:translateY(14px)} to{opacity:1;transform:translateY(0)} }
          @keyframes successIn{ from{opacity:0;transform:scale(.97)} to{opacity:1;transform:scale(1)} }
          @keyframes expandIn { from{opacity:0;transform:translateY(-8px)} to{opacity:1;transform:translateY(0)} }
          @keyframes pulseBorder { 0%,100%{border-color:rgba(245,158,11,.4)} 50%{border-color:rgba(245,158,11,.9)} }

          .pkg-hero{padding:140px 40px 60px;max-width:1100px;margin:0 auto;text-align:center;position:relative}
          .pkg-glow{position:absolute;width:700px;height:500px;border-radius:50%;background:radial-gradient(circle,rgba(37,99,235,.1) 0%,transparent 70%);top:0;left:50%;transform:translateX(-50%);pointer-events:none;animation:glow 7s ease-in-out infinite}
          .pkg-eyebrow{display:inline-flex;align-items:center;gap:8px;padding:5px 14px 5px 10px;background:rgba(37,99,235,.1);border:1px solid rgba(37,99,235,.25);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:11px;color:#60a5fa;letter-spacing:.08em;text-transform:uppercase;margin-bottom:24px;animation:fadeUp .5s ease both}
          .pkg-dot{width:6px;height:6px;background:#60a5fa;border-radius:50%;animation:blink 2s ease infinite}
          .pkg-title{font-family:'Outfit',sans-serif;font-size:clamp(30px,4.5vw,58px);font-weight:900;letter-spacing:-.04em;line-height:1.05;margin-bottom:16px;animation:fadeUp .6s ease .1s both}
          .pkg-title span{background:linear-gradient(135deg,#2563eb,#06b6d4,#22d3ee,#06b6d4,#2563eb);background-size:300%;-webkit-background-clip:text;-webkit-text-fill-color:transparent;background-clip:text;animation:gradShift 5s ease infinite}
          .pkg-sub{font-family:'Outfit',sans-serif;font-size:clamp(14px,1.5vw,17px);color:var(--muted);max-width:540px;margin:0 auto;line-height:1.8;animation:fadeUp .6s ease .2s both}

          .stepper-wrap{max-width:960px;margin:0 auto;padding:0 40px 48px;animation:fadeUp .5s ease .25s both}
          .stepper{display:flex;align-items:center}
          .step-item{display:flex;flex-direction:column;align-items:center;flex:1;position:relative}
          .step-item:not(:last-child)::after{content:'';position:absolute;top:16px;left:calc(50% + 20px);right:calc(-50% + 20px);height:1px;background:var(--border);z-index:0}
          .step-item:not(:last-child).done::after{background:linear-gradient(90deg,#2563eb,#06b6d4)}
          .step-circle{width:32px;height:32px;border-radius:50%;border:1px solid rgba(255,255,255,.12);background:rgba(255,255,255,.04);display:flex;align-items:center;justify-content:center;font-family:'JetBrains Mono',monospace;font-size:11px;color:var(--muted);transition:all .3s ease;position:relative;z-index:1}
          .step-item.active .step-circle{background:linear-gradient(135deg,#2563eb,#06b6d4);border-color:transparent;color:#fff;box-shadow:0 0 20px rgba(37,99,235,.4)}
          .step-item.done .step-circle{background:rgba(37,99,235,.2);border-color:rgba(37,99,235,.4);color:#60a5fa}
          .step-label{font-family:'Outfit',sans-serif;font-size:11px;color:var(--muted);margin-top:8px;font-weight:500;white-space:nowrap}
          .step-item.active .step-label{color:#fff;font-weight:700}
          .step-item.done .step-label{color:#60a5fa}

          .step-card{max-width:960px;margin:0 auto 40px;padding:0 40px;animation:slideIn .4s cubic-bezier(.4,0,.2,1) both}

          .step-card-inner{background:rgba(255,255,255,.02);border:1px solid var(--border);border-radius:24px;padding:40px;position:relative;overflow:visible}
          .step-card-inner::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(37,99,235,.5),rgba(6,182,212,.4),transparent);border-radius:24px 24px 0 0;pointer-events:none}
          .step-heading{font-family:'Outfit',sans-serif;font-size:20px;font-weight:800;color:#fff;letter-spacing:-.02em;margin-bottom:6px}
          .step-hint{font-family:'Outfit',sans-serif;font-size:13px;color:var(--muted);margin-bottom:28px;line-height:1.5}

          .project-grid{display:grid;grid-template-columns:repeat(4,1fr);gap:12px}
          .project-card{padding:20px 16px 16px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:14px;cursor:pointer;transition:all .25s ease;text-align:center;position:relative;overflow:visible}
          .project-card::before{content:'';position:absolute;inset:0;background:linear-gradient(135deg,rgba(37,99,235,.15),rgba(6,182,212,.08));opacity:0;transition:opacity .25s;border-radius:14px}
          .project-card:hover:not(.disabled){border-color:rgba(37,99,235,.3);transform:translateY(-3px)}
          .project-card:hover:not(.disabled)::before{opacity:1}
          .project-card.selected{border-color:rgba(37,99,235,.5);background:rgba(37,99,235,.1);transform:translateY(-3px);box-shadow:0 12px 32px rgba(37,99,235,.2)}
          .project-card.selected::before{opacity:1}
          .project-card.disabled{cursor:not-allowed;opacity:.38;filter:grayscale(0.6);border-color:rgba(255,255,255,.04);background:rgba(255,255,255,.01)}
          .project-card.disabled::after{content:'';position:absolute;inset:0;background:repeating-linear-gradient(45deg,rgba(255,255,255,.018) 0px,rgba(255,255,255,.018) 1px,transparent 1px,transparent 8px);border-radius:14px}
          .project-icon{font-size:26px;margin-bottom:8px;display:block}
          .project-label{font-family:'Outfit',sans-serif;font-size:13px;font-weight:700;color:#fff;margin-bottom:4px}
          .project-desc{font-family:'Outfit',sans-serif;font-size:11px;color:var(--muted);line-height:1.4}
          .project-check{position:absolute;top:8px;right:8px;width:16px;height:16px;background:linear-gradient(135deg,#2563eb,#06b6d4);border-radius:50%;display:flex;align-items:center;justify-content:center;font-size:8px;color:#fff;opacity:0;transition:opacity .2s}
          .project-card.selected .project-check{opacity:1}
          .project-base{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;margin-top:6px}
          .disabled-badge{display:inline-flex;align-items:center;gap:4px;margin-top:8px;padding:3px 8px;background:rgba(255,255,255,.06);border:1px solid rgba(255,255,255,.1);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:9px;color:rgba(255,255,255,.35);letter-spacing:.06em}

          .card-includes-toggle{display:inline-flex;align-items:center;gap:4px;margin-top:10px;padding:4px 10px;background:rgba(37,99,235,.1);border:1px solid rgba(37,99,235,.2);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:9px;color:#60a5fa;cursor:pointer;transition:all .2s;letter-spacing:.06em;text-transform:uppercase;position:relative;z-index:2}
          .card-includes-toggle:hover{background:rgba(37,99,235,.2);border-color:rgba(37,99,235,.4)}
          .card-includes-panel{position:absolute;top:calc(100% + 8px);left:50%;transform:translateX(-50%);width:240px;background:#0d1428;border:1px solid rgba(37,99,235,.3);border-radius:12px;padding:14px;z-index:200;animation:expandIn .2s ease;box-shadow:0 16px 40px rgba(0,0,0,.6);text-align:left}
          .card-includes-panel::before{content:'';position:absolute;top:-5px;left:50%;width:8px;height:8px;background:#0d1428;border-left:1px solid rgba(37,99,235,.3);border-top:1px solid rgba(37,99,235,.3);transform:translateX(-50%) rotate(45deg)}
          .cip-title{font-family:'JetBrains Mono',monospace;font-size:9px;color:#60a5fa;letter-spacing:.1em;text-transform:uppercase;margin-bottom:10px}
          .cip-item{display:flex;align-items:flex-start;gap:6px;font-family:'Outfit',sans-serif;font-size:11px;color:rgba(255,255,255,.65);line-height:1.5;margin-bottom:5px}
          .cip-dot{width:4px;height:4px;background:#2563eb;border-radius:50%;flex-shrink:0;margin-top:5px}

          .selected-includes-panel{margin-top:20px;padding:20px 24px;background:rgba(37,99,235,.05);border:1px solid rgba(37,99,235,.18);border-radius:16px;animation:expandIn .25s ease;position:relative;overflow:hidden}
          .selected-includes-panel::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(37,99,235,.5),rgba(6,182,212,.4),transparent)}
          .sip-header{display:flex;align-items:center;gap:10px;margin-bottom:14px}
          .sip-icon{font-size:20px}
          .sip-title{font-family:'Outfit',sans-serif;font-size:14px;font-weight:800;color:#fff}
          .sip-subtitle{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;margin-top:2px}
          .sip-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:6px}
          .sip-item{display:flex;align-items:flex-start;gap:7px;font-family:'Outfit',sans-serif;font-size:12px;color:rgba(255,255,255,.7);line-height:1.5;padding:7px 10px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.05);border-radius:8px}
          .sip-check{width:14px;height:14px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#06b6d4);display:flex;align-items:center;justify-content:center;font-size:7px;color:#fff;flex-shrink:0;margin-top:1px}

          .group-label{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;letter-spacing:.12em;text-transform:uppercase;margin:20px 0 10px;padding-bottom:6px;border-bottom:1px solid rgba(37,99,235,.15)}
          .group-label:first-child{margin-top:0}
          .features-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}
          .feature-item{display:flex;align-items:flex-start;gap:12px;padding:12px 14px;background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.06);border-radius:10px;cursor:pointer;transition:all .18s ease}
          .feature-item:hover{border-color:rgba(37,99,235,.3);background:rgba(37,99,235,.05)}
          .feature-item.selected{border-color:rgba(37,99,235,.4);background:rgba(37,99,235,.09)}
          .feature-item.base-included{cursor:not-allowed;border-color:rgba(37,99,235,.25);background:rgba(37,99,235,.06);opacity:.75}
          .feature-item.base-included:hover{border-color:rgba(37,99,235,.25);background:rgba(37,99,235,.06)}
          .base-included-badge{font-family:'JetBrains Mono',monospace;font-size:9px;color:#22d3ee;background:rgba(6,182,212,.12);border:1px solid rgba(6,182,212,.25);border-radius:100px;padding:2px 7px;white-space:nowrap;margin-top:3px;display:inline-block;letter-spacing:.06em}
          .f-check{width:16px;height:16px;border-radius:4px;border:1px solid rgba(255,255,255,.15);background:rgba(255,255,255,.04);display:flex;align-items:center;justify-content:center;font-size:9px;color:#fff;flex-shrink:0;transition:all .18s;margin-top:2px}
          .feature-item.selected .f-check{background:linear-gradient(135deg,#2563eb,#06b6d4);border-color:transparent}
          .feature-item.base-included .f-check{background:linear-gradient(135deg,#0ea5e9,#22d3ee);border-color:transparent}
          .f-name{font-family:'Outfit',sans-serif;font-size:12.5px;font-weight:600;color:rgba(255,255,255,.82)}
          .f-cost{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;margin-top:1px}
          .f-desc{font-family:'Outfit',sans-serif;font-size:11px;color:rgba(255,255,255,.38);margin-top:4px;line-height:1.45}

          .total-bar{position:fixed;top:72px;left:0;right:0;width:100%;z-index:100;background:rgba(4,6,16,.95);backdrop-filter:blur(20px);border-bottom:1px solid rgba(37,99,235,.2);padding:12px 40px;display:flex;align-items:center;justify-content:space-between;gap:12px;flex-wrap:wrap}
          .total-bar-label{font-family:'JetBrains Mono',monospace;font-size:10px;color:rgba(255,255,255,.4);letter-spacing:.1em;text-transform:uppercase}
          .total-bar-value{font-family:'Outfit',sans-serif;font-size:22px;font-weight:900;color:#fff;letter-spacing:-.03em}
          .total-bar-breakdown{display:flex;gap:16px;flex-wrap:wrap}
          .total-bar-item{font-family:'JetBrains Mono',monospace;font-size:10px;color:rgba(255,255,255,.35)}
          .total-bar-item span{color:#60a5fa}

          .options-grid{display:grid;gap:8px}
          .option-btn{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.07);border-radius:12px;cursor:pointer;transition:all .2s ease}
          .option-btn:hover{border-color:rgba(255,255,255,.14);background:rgba(255,255,255,.04)}
          .option-btn.selected{border-color:rgba(37,99,235,.45);background:rgba(37,99,235,.08);box-shadow:0 0 18px rgba(37,99,235,.1)}
          .opt-left{display:flex;align-items:center;gap:10px}
          .opt-dot{width:8px;height:8px;border-radius:50%;flex-shrink:0}
          .opt-label{font-family:'Outfit',sans-serif;font-size:14px;font-weight:700;color:#fff}
          .opt-sub{font-family:'Outfit',sans-serif;font-size:12px;color:var(--muted);margin-top:1px}
          .opt-weeks{font-family:'JetBrains Mono',monospace;font-size:10px;color:#22d3ee;margin-top:3px;letter-spacing:.04em}
          .opt-right{display:flex;align-items:center;gap:8px}
          .opt-badge{font-family:'JetBrains Mono',monospace;font-size:9px;padding:3px 8px;background:rgba(245,158,11,.1);border:1px solid rgba(245,158,11,.25);border-radius:100px;color:#fbbf24}
          .opt-add{font-family:'JetBrains Mono',monospace;font-size:11px;color:rgba(255,255,255,.3)}
          .option-btn.selected .opt-add{color:#60a5fa}

          .addons-grid{display:grid;grid-template-columns:repeat(2,1fr);gap:8px}
          .addon-item{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;background:rgba(255,255,255,.02);border:1px solid rgba(255,255,255,.06);border-radius:10px;cursor:pointer;transition:all .18s ease}
          .addon-item:hover{border-color:rgba(6,182,212,.3);background:rgba(6,182,212,.05)}
          .addon-item.selected{border-color:rgba(6,182,212,.4);background:rgba(6,182,212,.08)}
          .addon-item.selected .f-check{background:linear-gradient(135deg,#06b6d4,#22d3ee);border-color:transparent}

          .explainer-toggle{display:inline-flex;align-items:center;gap:6px;margin-bottom:12px;padding:5px 12px;background:rgba(37,99,235,.08);border:1px solid rgba(37,99,235,.2);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:9px;color:#60a5fa;cursor:pointer;transition:all .2s;letter-spacing:.07em;text-transform:uppercase;user-select:none}
          .explainer-toggle:hover{background:rgba(37,99,235,.16);border-color:rgba(37,99,235,.4)}
          .explainer-toggle-icon{font-size:11px;transition:transform .25s;display:inline-block}
          .explainer-toggle-icon.open{transform:rotate(180deg)}
          .explainer-panel{margin-bottom:16px;padding:20px 22px;background:rgba(37,99,235,.05);border:1px solid rgba(37,99,235,.18);border-radius:14px;animation:expandIn .25s ease;position:relative;overflow:hidden}
          .explainer-panel::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(37,99,235,.5),rgba(6,182,212,.4),transparent)}
          .explainer-panel-title{font-family:'Outfit',sans-serif;font-size:13px;font-weight:800;color:#fff;margin-bottom:4px}
          .explainer-panel-sub{font-family:'Outfit',sans-serif;font-size:12px;color:rgba(255,255,255,.45);line-height:1.6;margin-bottom:16px}
          .explainer-points{display:grid;grid-template-columns:repeat(2,1fr);gap:10px}
          .explainer-point{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.05);border-radius:10px}
          .explainer-point-icon{font-size:18px;flex-shrink:0;margin-top:1px}
          .explainer-point-title{font-family:'Outfit',sans-serif;font-size:12px;font-weight:700;color:rgba(255,255,255,.85);margin-bottom:3px}
          .explainer-point-desc{font-family:'Outfit',sans-serif;font-size:11px;color:rgba(255,255,255,.4);line-height:1.55}

          .timeline-explainer-toggle{display:inline-flex;align-items:center;gap:6px;margin-bottom:12px;padding:5px 12px;background:rgba(245,158,11,.08);border:1px solid rgba(245,158,11,.2);border-radius:100px;font-family:'JetBrains Mono',monospace;font-size:9px;color:#fbbf24;cursor:pointer;transition:all .2s;letter-spacing:.07em;text-transform:uppercase;user-select:none}
          .timeline-explainer-toggle:hover{background:rgba(245,158,11,.16);border-color:rgba(245,158,11,.4)}
          .timeline-explainer-toggle-icon{font-size:11px;transition:transform .25s;display:inline-block}
          .timeline-explainer-toggle-icon.open{transform:rotate(180deg)}
          .timeline-explainer-panel{margin-bottom:16px;padding:20px 22px;background:rgba(245,158,11,.04);border:1px solid rgba(245,158,11,.18);border-radius:14px;animation:expandIn .25s ease;position:relative;overflow:hidden}
          .timeline-explainer-panel::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(245,158,11,.5),rgba(251,191,36,.4),transparent)}
          .timeline-explainer-point{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;background:rgba(255,255,255,.025);border:1px solid rgba(255,255,255,.05);border-radius:10px}

          .pdf-export-banner{padding:18px 22px;margin-bottom:20px;background:rgba(245,158,11,.06);border:1px solid rgba(245,158,11,.3);border-radius:14px;display:flex;align-items:center;gap:16px;animation:pulseBorder 2.5s ease infinite;position:relative;overflow:hidden}
          .pdf-export-banner::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(245,158,11,.6),transparent)}
          .pdf-banner-icon{font-size:28px;flex-shrink:0}
          .pdf-banner-text-title{font-family:'Outfit',sans-serif;font-size:14px;font-weight:800;color:#fbbf24;margin-bottom:3px}
          .pdf-banner-text-sub{font-family:'Outfit',sans-serif;font-size:12px;color:rgba(251,191,36,.65);line-height:1.5}
          .pdf-banner-right{margin-left:auto;flex-shrink:0}
          .btn-export-pdf{padding:10px 20px;background:linear-gradient(135deg,#d97706,#f59e0b);border:none;border-radius:10px;font-family:'Outfit',sans-serif;font-weight:700;font-size:13px;color:#fff;cursor:pointer;transition:all .25s ease;box-shadow:0 4px 16px rgba(245,158,11,.3);white-space:nowrap}
          .btn-export-pdf:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 8px 24px rgba(245,158,11,.5)}
          .btn-export-pdf:disabled{opacity:.6;cursor:wait}
          .btn-export-pdf-success{padding:10px 20px;background:rgba(34,197,94,.15);border:1px solid rgba(34,197,94,.3);border-radius:10px;font-family:'Outfit',sans-serif;font-weight:700;font-size:13px;color:#4ade80;cursor:pointer;transition:all .25s ease;white-space:nowrap}
          .btn-export-pdf-success:hover{background:rgba(34,197,94,.25)}

          .quote-box{background:rgba(255,255,255,.015);border:1px solid var(--border);border-radius:16px;padding:24px;margin-bottom:20px}
          .q-row{display:flex;justify-content:space-between;align-items:flex-start;padding:9px 0;border-bottom:1px solid rgba(255,255,255,.04);gap:12px}
          .q-row:last-child{border-bottom:none}
          .q-key{font-family:'JetBrains Mono',monospace;font-size:10px;color:var(--muted);letter-spacing:.07em;text-transform:uppercase;flex-shrink:0;padding-top:1px}
          .q-val{font-family:'Outfit',sans-serif;font-size:13px;color:rgba(255,255,255,.8);font-weight:500;text-align:right;line-height:1.5}
          .q-cost{font-family:'JetBrains Mono',monospace;font-size:11px;color:#60a5fa;margin-left:8px;flex-shrink:0}
          .quote-total-box{background:linear-gradient(135deg,rgba(37,99,235,.14),rgba(6,182,212,.09));border:1px solid rgba(37,99,235,.3);border-radius:14px;padding:24px;text-align:center;margin-top:16px;position:relative;overflow:hidden}
          .quote-total-box::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(37,99,235,.7),rgba(6,182,212,.6),transparent)}
          .qt-label{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;letter-spacing:.14em;text-transform:uppercase;margin-bottom:10px}
          .qt-value{font-family:'Outfit',sans-serif;font-size:clamp(28px,4vw,44px);font-weight:900;color:#fff;letter-spacing:-.04em}
          .qt-note{font-family:'Outfit',sans-serif;font-size:12px;color:var(--muted);margin-top:8px}

          .base-includes-accordion{margin-bottom:20px;border:1px solid rgba(37,99,235,.2);border-radius:14px;overflow:hidden}
          .bia-header{display:flex;align-items:center;justify-content:space-between;padding:14px 18px;background:rgba(37,99,235,.07);cursor:pointer;transition:background .2s;user-select:none}
          .bia-header:hover{background:rgba(37,99,235,.12)}
          .bia-left{display:flex;align-items:center;gap:10px}
          .bia-icon{font-size:16px}
          .bia-title{font-family:'Outfit',sans-serif;font-size:13px;font-weight:700;color:#fff}
          .bia-subtitle{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;margin-top:1px}
          .bia-chevron{font-size:11px;color:#60a5fa;transition:transform .25s;display:inline-block}
          .bia-chevron.open{transform:rotate(180deg)}
          .bia-body{padding:16px 18px;background:rgba(37,99,235,.03);border-top:1px solid rgba(37,99,235,.15);display:grid;grid-template-columns:repeat(2,1fr);gap:6px;animation:expandIn .2s ease}
          .bia-item{display:flex;align-items:flex-start;gap:7px;font-family:'Outfit',sans-serif;font-size:12px;color:rgba(255,255,255,.7);line-height:1.5}
          .bia-check{width:14px;height:14px;border-radius:50%;background:linear-gradient(135deg,#2563eb,#06b6d4);display:flex;align-items:center;justify-content:center;font-size:7px;color:#fff;flex-shrink:0;margin-top:1px}

          .form-row{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px}
          .form-group{display:flex;flex-direction:column;gap:6px;margin-bottom:14px}
          .form-label{font-family:'JetBrains Mono',monospace;font-size:10px;color:rgba(255,255,255,.4);letter-spacing:.1em;text-transform:uppercase}
          .form-input{box-sizing:border-box;width:100%;padding:12px 14px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:10px;font-family:'Outfit',sans-serif;font-size:14px;color:#fff;outline:none;transition:border-color .2s,background .2s}
          .form-input::placeholder{color:rgba(255,255,255,.2)}
          .form-input:focus{border-color:rgba(37,99,235,.5);background:rgba(37,99,235,.06)}
          .form-input.error{border-color:rgba(239,68,68,.5);}
          .form-textarea{box-sizing:border-box;width:100%;padding:12px 14px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:10px;font-family:'Outfit',sans-serif;font-size:14px;color:#fff;outline:none;transition:border-color .2s;resize:vertical;min-height:90px}
          .form-textarea::placeholder{color:rgba(255,255,255,.2)}
          .form-textarea:focus{border-color:rgba(37,99,235,.5);background:rgba(37,99,235,.06)}

          .btn-row{display:flex;gap:10px;justify-content:flex-end;margin-top:24px}
          .btn-back{padding:11px 22px;background:rgba(255,255,255,.04);border:1px solid rgba(255,255,255,.1);border-radius:10px;font-family:'Outfit',sans-serif;font-weight:600;font-size:13px;color:rgba(255,255,255,.55);cursor:pointer;transition:all .2s}
          .btn-back:hover{background:rgba(255,255,255,.07);color:#fff}
          .btn-next{padding:11px 26px;background:linear-gradient(135deg,#2563eb,#06b6d4);border:none;border-radius:10px;font-family:'Outfit',sans-serif;font-weight:700;font-size:13px;color:#fff;cursor:pointer;transition:all .3s ease;box-shadow:0 6px 20px rgba(37,99,235,.3)}
          .btn-next:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 10px 28px rgba(37,99,235,.5)}
          .btn-next:disabled{opacity:.4;cursor:not-allowed}
          .btn-submit{width:100%;padding:14px;background:linear-gradient(135deg,#2563eb,#06b6d4);border:none;border-radius:12px;font-family:'Outfit',sans-serif;font-weight:700;font-size:15px;color:#fff;cursor:pointer;box-shadow:0 8px 24px rgba(37,99,235,.3);transition:all .3s ease;margin-top:6px}
          .btn-submit:hover:not(:disabled){transform:translateY(-2px);box-shadow:0 12px 36px rgba(37,99,235,.55)}
          .btn-submit:disabled{opacity:.5;cursor:not-allowed}

          .msg-success{padding:12px 16px;background:rgba(34,197,94,.1);border:1px solid rgba(34,197,94,.25);border-radius:10px;font-family:'Outfit',sans-serif;font-size:13px;color:#4ade80;margin-top:12px;text-align:center;animation:successIn .35s ease}
          .msg-error{padding:12px 16px;background:rgba(239,68,68,.1);border:1px solid rgba(239,68,68,.25);border-radius:10px;font-family:'Outfit',sans-serif;font-size:13px;color:#f87171;margin-top:12px;text-align:center}
          .msg-missing{padding:12px 16px;background:rgba(234,179,8,.1);border:1px solid rgba(234,179,8,.25);border-radius:10px;font-family:'Outfit',sans-serif;font-size:13px;color:#fbbf24;margin-top:12px;text-align:center}

          .notes-wrap{max-width:960px;margin:0 auto 80px;padding:0 40px}
          .notes-inner{padding:22px 28px;background:rgba(255,255,255,.015);border:1px solid var(--border);border-radius:16px;position:relative;overflow:hidden}
          .notes-inner::before{content:'';position:absolute;top:0;left:0;right:0;height:1px;background:linear-gradient(90deg,transparent,rgba(37,99,235,.4),rgba(6,182,212,.3),transparent)}
          .notes-title{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;letter-spacing:.15em;text-transform:uppercase;margin-bottom:12px}
          .note-item{display:flex;align-items:flex-start;gap:10px;font-family:'Outfit',sans-serif;font-size:13px;color:var(--muted);line-height:1.6;margin-bottom:8px}
          .note-dot{width:4px;height:4px;background:#2563eb;border-radius:50%;flex-shrink:0;margin-top:8px}

          .pkg-footer{border-top:1px solid rgba(255,255,255,.06);padding:26px 40px;max-width:1280px;margin:0 auto;display:flex;align-items:center;justify-content:space-between;flex-wrap:wrap;gap:12px}
          .pkg-footer-brand{font-family:'Outfit',sans-serif;font-size:13px;font-weight:700;color:rgba(255,255,255,.35)}
          .pkg-footer-right{font-family:'Outfit',sans-serif;font-size:12px;color:var(--muted)}
          .pkg-footer-right a{color:#60a5fa;text-decoration:none}
          .section-tag{font-family:'JetBrains Mono',monospace;font-size:10px;color:#60a5fa;letter-spacing:.12em;text-transform:uppercase;margin-bottom:12px;display:block}

          @media(max-width:900px){
            .project-grid{grid-template-columns:repeat(2,1fr)}
            .features-grid,.addons-grid{grid-template-columns:1fr}
            .form-row{grid-template-columns:1fr}
            .sip-grid{grid-template-columns:repeat(2,1fr)}
            .bia-body{grid-template-columns:1fr}
            .pdf-export-banner{flex-wrap:wrap}
            .pdf-banner-right{margin-left:0;width:100%}
            .btn-export-pdf,.btn-export-pdf-success{width:100%}
            .explainer-points{grid-template-columns:1fr}
          }
          @media(max-width:640px){
            .pkg-hero{padding:120px 20px 40px}
            .stepper-wrap,.step-card,.notes-wrap{padding-left:20px;padding-right:20px}
            .step-card-inner{padding:22px 16px}
            .step-label{display:none}
            .total-bar{padding:10px 20px}
            .total-bar-breakdown{display:none}
            .sip-grid{grid-template-columns:1fr}
            .card-includes-panel{width:200px}
          }
        `}</style>

        <Navbar />
        <Helmet>
          <title>Build Your Quote | MDS Software Development Services</title>
          <meta
            name="description"
            content="Select exactly what you need and get a real-time exact price — no ranges, no guessing. MDS custom software quote builder."
          />
          <meta
            property="og:title"
            content="Build Your Quote | MDS Software Development Services"
          />
          <meta
            property="og:url"
            content="https://mdstechservices-ph.online/packages"
          />
        </Helmet>

        {/* HERO */}
        <section className="pkg-hero">
          <div className="pkg-glow" />
          <div className="pkg-eyebrow">
            <span className="pkg-dot" /> Exact Pricing · You Choose What You
            Need
          </div>
          <h1 className="pkg-title">
            Build Your <span>Exact Quote</span>
          </h1>
          <p className="pkg-sub">
            Pick your project type, select the features you need, choose your
            scale — and get your exact total price instantly.
          </p>
        </section>

        {/* ── STEPPER ── */}
        <div className="stepper-wrap" ref={stepperRef}>
          <div className="stepper">
            {STEPS.map((s, i) => (
              <div
                key={i}
                className={`step-item ${i === step ? "active" : i < step ? "done" : ""}`}
              >
                <div className="step-circle">{i < step ? "✓" : i + 1}</div>
                <div className="step-label">{s}</div>
              </div>
            ))}
          </div>
        </div>

        <div style={{ height: "62px" }} />

        {/* LIVE TOTAL BAR */}
        <div className="total-bar">
          <div>
            <div className="total-bar-label">Running Total</div>
            <div className="total-bar-value">{php(grandTotal)}</div>
          </div>
          <div className="total-bar-breakdown">
            <div className="total-bar-item">
              Base <span>{php(basePrice)}</span>
            </div>
            <div className="total-bar-item">
              Features <span>{php(featuresTotal)}</span>
            </div>
            {scaleAdd > 0 && (
              <div className="total-bar-item">
                Scale <span>+{php(scaleAdd)}</span>
              </div>
            )}
            {timelineAdd > 0 && (
              <div className="total-bar-item">
                Timeline <span>+{php(timelineAdd)}</span>
              </div>
            )}
            {addonsTotal > 0 && (
              <div className="total-bar-item">
                Add-ons <span>+{php(addonsTotal)}</span>
              </div>
            )}
          </div>
        </div>

        {/* STEP 0 */}
        {step === 0 && (
          <div className="step-card" key="s0">
            <div className="step-card-inner">
              <div className="step-heading">What are you building?</div>
              <div className="step-hint">
                Each project type has a base price. Click{" "}
                <strong style={{ color: "#60a5fa" }}>What's Included</strong> on
                any card to see what's covered before adding features.
              </div>
              <div className="project-grid">
                {PROJECT_TYPES.map((p) => (
                  <div
                    key={p.id}
                    className={`project-card ${projectId === p.id ? "selected" : ""} ${p.disabled ? "disabled" : ""}`}
                    onClick={() => {
                      if (!p.disabled) {
                        setProjectId(p.id);
                        setExpandedCard(null);
                      }
                    }}
                    style={{ position: "relative" }}
                  >
                    {!p.disabled && <div className="project-check">✓</div>}
                    <span className="project-icon">{p.icon}</span>
                    <div className="project-label">{p.label}</div>
                    <div className="project-desc">{p.desc}</div>
                    {p.disabled ? (
                      <div className="disabled-badge">🔒 Coming Soon</div>
                    ) : (
                      <>
                        <div className="project-base">Base: {php(p.base)}</div>
                        <div
                          ref={expandedCard === p.id ? cardPanelRef : null}
                          style={{ position: "relative" }}
                        >
                          <div
                            className="card-includes-toggle"
                            onClick={(e) => {
                              e.stopPropagation();
                              setExpandedCard(
                                expandedCard === p.id ? null : p.id,
                              );
                            }}
                          >
                            {expandedCard === p.id ? "▴" : "▾"} What's Included
                          </div>
                          {expandedCard === p.id && (
                            <div
                              className="card-includes-panel"
                              onClick={(e) => e.stopPropagation()}
                            >
                              <div className="cip-title">Base Includes</div>
                              {p.includes.map((item, i) => (
                                <div key={i} className="cip-item">
                                  <div className="cip-dot" />
                                  {item}
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
              {project && !project.disabled && project.includes.length > 0 && (
                <div className="selected-includes-panel">
                  <div className="sip-header">
                    <span className="sip-icon">{project.icon}</span>
                    <div>
                      <div className="sip-title">
                        {project.label} — Base Package Includes
                      </div>
                      <div className="sip-subtitle">
                        Base price: {php(project.base)} · Everything listed
                        below is already covered
                      </div>
                    </div>
                  </div>
                  <div className="sip-grid">
                    {project.includes.map((item, i) => (
                      <div key={i} className="sip-item">
                        <div className="sip-check">✓</div>
                        {item}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              <div className="btn-row">
                <button
                  className="btn-next"
                  disabled={!projectId}
                  onClick={() => setStep(1)}
                >
                  Next: Select Features →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 1 */}
        {step === 1 && (
          <div className="step-card" key="s1">
            <div className="step-card-inner">
              <div className="step-heading">
                {project?.icon} {project?.label} — Select Features
              </div>
              <div className="step-hint">
                Items marked{" "}
                <strong style={{ color: "#22d3ee" }}>Base Included</strong> are
                already covered in the base price and cannot be removed. Check
                any additional features you want — prices shown are exact.
              </div>
              {Object.entries(groupFeatures(features)).map(([grp, fList]) => (
                <div key={grp}>
                  <div className="group-label">{grp}</div>
                  <div className="features-grid">
                    {fList.map((f) => {
                      const isBaseIncluded = f.cost === 0;
                      const isSelected = selectedFeatures.includes(f.id);
                      return (
                        <div
                          key={f.id}
                          className={`feature-item ${isBaseIncluded ? "base-included" : isSelected ? "selected" : ""}`}
                          onClick={() => !isBaseIncluded && toggleFeature(f.id)}
                          title={
                            isBaseIncluded
                              ? "Already included in the base package"
                              : undefined
                          }
                        >
                          <div className="f-check">
                            {isBaseIncluded || isSelected ? "✓" : ""}
                          </div>
                          <div style={{ flex: 1 }}>
                            <div className="f-name">{f.label}</div>
                            {isBaseIncluded ? (
                              <span className="base-included-badge">
                                🔒 Base Included
                              </span>
                            ) : (
                              <div className="f-cost">+{php(f.cost)}</div>
                            )}
                            {f.desc && <div className="f-desc">{f.desc}</div>}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
              <div className="btn-row">
                <button className="btn-back" onClick={() => setStep(0)}>
                  ← Back
                </button>
                <button className="btn-next" onClick={() => setStep(2)}>
                  Next: Scale & Add-ons →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 2 */}
        {step === 2 && (
          <div className="step-card" key="s2">
            <div className="step-card-inner">
              <div className="step-heading">Scale, Timeline & Add-ons</div>
              <div className="step-hint">
                These affect your final price. All values are exact — no
                multipliers, just fixed add-ons.
              </div>

              <span className="section-tag">User Scale</span>
              <div
                className="explainer-toggle"
                onClick={() => setScaleExplainerOpen(!scaleExplainerOpen)}
              >
                <span
                  className={`explainer-toggle-icon ${scaleExplainerOpen ? "open" : ""}`}
                >
                  ▾
                </span>
                Why does user count affect the price?
              </div>
              {scaleExplainerOpen && (
                <div className="explainer-panel">
                  <div className="explainer-panel-title">
                    How many users changes how we build your system
                  </div>
                  <div className="explainer-panel-sub">
                    The number of people using your system simultaneously is one
                    of the most important architectural decisions we make. A
                    system built for 5 users is fundamentally different from one
                    built for 5,000 — and the extra cost reflects real
                    additional engineering work, not just a pricing tier.
                  </div>
                  <div className="explainer-points">
                    {SCALE_POINTS.map((pt, i) => (
                      <div key={i} className="explainer-point">
                        <div className="explainer-point-icon">{pt.icon}</div>
                        <div>
                          <div className="explainer-point-title">
                            {pt.title}
                          </div>
                          <div className="explainer-point-desc">{pt.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      padding: "10px 14px",
                      background: "rgba(37,99,235,.08)",
                      border: "1px solid rgba(37,99,235,.2)",
                      borderRadius: 8,
                      fontFamily: "'Outfit',sans-serif",
                      fontSize: 12,
                      color: "rgba(255,255,255,.55)",
                      lineHeight: 1.6,
                    }}
                  >
                    💡{" "}
                    <strong style={{ color: "rgba(255,255,255,.75)" }}>
                      Not sure which to pick?
                    </strong>{" "}
                    Choose the scale that reflects your realistic active users
                    at launch — you can always scale up your hosting
                    infrastructure later, but getting the architecture right
                    from day one saves expensive rewrites.
                  </div>
                </div>
              )}
              <div
                className="options-grid"
                style={{
                  gridTemplateColumns: "repeat(2,1fr)",
                  marginBottom: 28,
                }}
              >
                {SCALES.map((s) => (
                  <div
                    key={s.id}
                    className={`option-btn ${scaleId === s.id ? "selected" : ""}`}
                    onClick={() => setScaleId(s.id)}
                  >
                    <div className="opt-left">
                      <div
                        className="opt-dot"
                        style={{ background: s.color }}
                      />
                      <div>
                        <div className="opt-label">{s.label}</div>
                        <div className="opt-sub">{s.sub}</div>
                      </div>
                    </div>
                    <span className="opt-add">
                      {s.add === 0 ? "Included" : "+" + php(s.add)}
                    </span>
                  </div>
                ))}
              </div>

              <span className="section-tag">Delivery Timeline</span>
              <div
                className="timeline-explainer-toggle"
                onClick={() => setTimelineExplainerOpen(!timelineExplainerOpen)}
              >
                <span
                  className={`timeline-explainer-toggle-icon ${timelineExplainerOpen ? "open" : ""}`}
                >
                  ▾
                </span>
                Why does a faster timeline cost more?
              </div>
              {timelineExplainerOpen && (
                <div className="timeline-explainer-panel">
                  <div className="explainer-panel-title">
                    Speed requires more resources, not less
                  </div>
                  <div className="explainer-panel-sub">
                    A rush fee isn't padding — it reflects the real cost of
                    compressing a project. Faster delivery means more developers
                    working in parallel, tighter QA cycles, and your project
                    taking priority in our active queue ahead of other work.
                  </div>
                  <div className="explainer-points">
                    {TIMELINE_POINTS.map((pt, i) => (
                      <div key={i} className="timeline-explainer-point">
                        <div className="explainer-point-icon">{pt.icon}</div>
                        <div>
                          <div className="explainer-point-title">
                            {pt.title}
                          </div>
                          <div className="explainer-point-desc">{pt.desc}</div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <div
                    style={{
                      marginTop: 14,
                      padding: "10px 14px",
                      background: "rgba(245,158,11,.07)",
                      border: "1px solid rgba(245,158,11,.2)",
                      borderRadius: 8,
                      fontFamily: "'Outfit',sans-serif",
                      fontSize: 12,
                      color: "rgba(255,255,255,.55)",
                      lineHeight: 1.6,
                    }}
                  >
                    💡{" "}
                    <strong style={{ color: "rgba(255,255,255,.75)" }}>
                      When to choose Flexible:
                    </strong>{" "}
                    If you don't have a hard deadline, Flexible is always the
                    best value. The system quality is identical — you're only
                    paying extra for speed, not for a better product.
                  </div>
                </div>
              )}

              <div className="options-grid" style={{ marginBottom: 28 }}>
                {TIMELINES.map((t) => (
                  <div
                    key={t.id}
                    className={`option-btn ${timelineId === t.id ? "selected" : ""}`}
                    onClick={() => setTimelineId(t.id)}
                  >
                    <div className="opt-left">
                      <div
                        className="opt-dot"
                        style={{ background: "#2563eb" }}
                      />
                      <div>
                        <div className="opt-label">{t.label}</div>
                        <div className="opt-sub">{t.sub}</div>
                        <div className="opt-weeks">⏱ {t.weeks}</div>
                      </div>
                    </div>
                    <div className="opt-right">
                      {t.badge && <span className="opt-badge">{t.badge}</span>}
                      <span className="opt-add">
                        {t.add === 0 ? "Included" : "+" + php(t.add)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              <span className="section-tag">Optional Add-ons</span>
              <div className="addons-grid">
                {ADDONS.map((a) => (
                  <div
                    key={a.id}
                    className={`addon-item ${selectedAddons.includes(a.id) ? "selected" : ""}`}
                    onClick={() => toggleAddon(a.id)}
                  >
                    <div className="f-check" style={{ marginTop: 2 }}>
                      {selectedAddons.includes(a.id) ? "✓" : ""}
                    </div>
                    <div style={{ flex: 1 }}>
                      <div className="f-name">{a.label}</div>
                      <div className="f-cost">+{php(a.cost)}</div>
                      {a.desc && <div className="f-desc">{a.desc}</div>}
                    </div>
                  </div>
                ))}
              </div>
              <div className="btn-row">
                <button className="btn-back" onClick={() => setStep(1)}>
                  ← Back
                </button>
                <button className="btn-next" onClick={() => setStep(3)}>
                  View My Exact Quote →
                </button>
              </div>
            </div>
          </div>
        )}

        {/* STEP 3 */}
        {step === 3 && (
          <div className="step-card" key="s3">
            <div className="step-card-inner">
              {status === "success" ? (
                <div
                  style={{
                    textAlign: "center",
                    padding: "40px 0",
                    animation: "successIn .4s ease",
                  }}
                >
                  <div style={{ fontSize: 48, marginBottom: 16 }}>✅</div>
                  <div
                    style={{
                      fontFamily: "'Outfit',sans-serif",
                      fontSize: 22,
                      fontWeight: 800,
                      color: "#fff",
                      marginBottom: 10,
                    }}
                  >
                    Quote Submitted!
                  </div>
                  <div
                    style={{
                      fontFamily: "'Outfit',sans-serif",
                      fontSize: 14,
                      color: "var(--muted)",
                      lineHeight: 1.7,
                    }}
                  >
                    We've received your exact requirements and pricing.
                    <br />
                    We'll reach out within 24 hours to confirm and proceed.
                  </div>
                  <button
                    className="btn-next"
                    style={{ marginTop: 28 }}
                    onClick={() => {
                      setStep(0);
                      setProjectId(null);
                      setSelectedFeatures([]);
                      setScaleId("small");
                      setTimelineId("flexible");
                      setSelectedAddons([]);
                      setStatus("");
                      setExpandedCard(null);
                      setPdfExported(false);
                      setQuoteIncludesOpen(false);
                      setScaleExplainerOpen(false);
                      setTimelineExplainerOpen(false);
                    }}
                  >
                    Start a New Quote
                  </button>
                </div>
              ) : (
                <>
                  <div className="step-heading">Your Exact Quote</div>
                  <div className="step-hint">
                    This is your exact price based on everything you selected —
                    no surprises.
                  </div>

                  <div className="pdf-export-banner">
                    <div className="pdf-banner-icon">📄</div>
                    <div style={{ flex: 1 }}>
                      <div className="pdf-banner-text-title">
                        {pdfExported
                          ? "Quote PDF Downloaded!"
                          : "Export Your Detailed Quote PDF"}
                      </div>
                      <div className="pdf-banner-text-sub">
                        {pdfExported
                          ? "Your PDF includes a full breakdown with descriptions for every selected feature. You can re-export anytime."
                          : "Export to PDF to see a detailed description for each feature you selected — what it does and why it matters for your project."}
                      </div>
                    </div>
                    <div className="pdf-banner-right">
                      {pdfExported ? (
                        <button
                          className="btn-export-pdf-success"
                          onClick={handleExportPDF}
                        >
                          ↓ Download Again
                        </button>
                      ) : (
                        <button
                          className="btn-export-pdf"
                          onClick={handleExportPDF}
                          disabled={pdfGenerating}
                        >
                          {pdfGenerating ? "Generating..." : "📥 Export PDF"}
                        </button>
                      )}
                    </div>
                  </div>

                  {project?.includes?.length > 0 && (
                    <div className="base-includes-accordion">
                      <div
                        className="bia-header"
                        onClick={() => setQuoteIncludesOpen(!quoteIncludesOpen)}
                      >
                        <div className="bia-left">
                          <span className="bia-icon">{project.icon}</span>
                          <div>
                            <div className="bia-title">
                              Base Package — What's Already Included
                            </div>
                            <div className="bia-subtitle">
                              {project.includes.length} items covered in the{" "}
                              {php(basePrice)} base price
                            </div>
                          </div>
                        </div>
                        <span
                          className={`bia-chevron ${quoteIncludesOpen ? "open" : ""}`}
                        >
                          ▼
                        </span>
                      </div>
                      {quoteIncludesOpen && (
                        <div className="bia-body">
                          {project.includes.map((item, i) => (
                            <div key={i} className="bia-item">
                              <div className="bia-check">✓</div>
                              {item}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  )}

                  <div className="quote-box">
                    <div className="q-row">
                      <span className="q-key">Project Base</span>
                      <div style={{ display: "flex", alignItems: "center" }}>
                        <span className="q-val">
                          {project?.icon} {project?.label}
                        </span>
                        <span className="q-cost">{php(basePrice)}</span>
                      </div>
                    </div>
                    {features
                      .filter(
                        (f) => selectedFeatures.includes(f.id) && f.cost > 0,
                      )
                      .map((f) => (
                        <div className="q-row" key={f.id}>
                          <span className="q-key">Feature</span>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <span className="q-val">{f.label}</span>
                            <span className="q-cost">+{php(f.cost)}</span>
                          </div>
                        </div>
                      ))}
                    {scale.add > 0 && (
                      <div className="q-row">
                        <span className="q-key">Scale</span>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span className="q-val">
                            {scale.label} — {scale.sub}
                          </span>
                          <span className="q-cost">+{php(scale.add)}</span>
                        </div>
                      </div>
                    )}
                    {timeline.add > 0 && (
                      <div className="q-row">
                        <span className="q-key">Timeline</span>
                        <div style={{ display: "flex", alignItems: "center" }}>
                          <span className="q-val">
                            {timeline.label} ({timeline.weeks}) — {timeline.sub}
                          </span>
                          <span className="q-cost">+{php(timeline.add)}</span>
                        </div>
                      </div>
                    )}
                    {ADDONS.filter((a) => selectedAddons.includes(a.id)).map(
                      (a) => (
                        <div className="q-row" key={a.id}>
                          <span className="q-key">Add-on</span>
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <span className="q-val">{a.label}</span>
                            <span className="q-cost">+{php(a.cost)}</span>
                          </div>
                        </div>
                      ),
                    )}
                    <div className="quote-total-box">
                      <div className="qt-label">Your Exact Total</div>
                      <div className="qt-value">{php(grandTotal)}</div>
                      <div className="qt-note">
                        50% downpayment ({php(grandTotal / 2)}) to start ·
                        Balance upon completion
                      </div>
                    </div>
                  </div>

                  <span className="section-tag">Your Contact Details</span>
                  <div className="form-row">
                    <div className="form-group">
                      <label className="form-label">Full Name *</label>
                      <input
                        className={`form-input${status === "missing" && !form.name.trim() ? " error" : ""}`}
                        type="text"
                        placeholder="Juan dela Cruz"
                        value={form.name}
                        onChange={(e) =>
                          setForm({ ...form, name: e.target.value })
                        }
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">Email Address *</label>
                      <input
                        className={`form-input${status === "invalidemail" ? " error" : ""}`}
                        type="email"
                        placeholder="juan@email.com"
                        value={form.email}
                        onChange={(e) =>
                          setForm({ ...form, email: e.target.value })
                        }
                      />
                    </div>
                  </div>
                  <div className="form-group">
                    <label className="form-label">Company / Organization</label>
                    <input
                      className="form-input"
                      type="text"
                      placeholder="Optional"
                      value={form.company}
                      onChange={(e) =>
                        setForm({ ...form, company: e.target.value })
                      }
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Additional Notes</label>
                    <textarea
                      className="form-textarea"
                      placeholder="Anything else we should know?"
                      value={form.message}
                      onChange={(e) =>
                        setForm({ ...form, message: e.target.value })
                      }
                    />
                  </div>
                  <button
                    className="btn-submit"
                    onClick={handleSubmit}
                    disabled={
                      loading ||
                      !form.name.trim() ||
                      !form.email.trim() ||
                      !isValidEmail(form.email.trim())
                    }
                  >
                    {loading ? "Sending..." : "Submit My Quote →"}
                  </button>
                  {status === "error" && (
                    <div className="msg-error">
                      Something went wrong. Please try again.
                    </div>
                  )}
                  {status === "missing" && (
                    <div className="msg-missing">
                      Please fill in your full name.
                    </div>
                  )}
                  {status === "invalidemail" && (
                    <div className="msg-missing">
                      Please enter a valid email address (e.g. juan@email.com).
                    </div>
                  )}

                  <div className="btn-row" style={{ marginTop: 14 }}>
                    <button
                      className="btn-back"
                      onClick={() => {
                        setStatus("");
                        setStep(2);
                      }}
                    >
                      ← Adjust Selections
                    </button>
                  </div>
                </>
              )}
            </div>
          </div>
        )}

        {/* NOTES */}
        <div className="notes-wrap">
          <div className="notes-inner">
            <div className="notes-title">{"// important notes"}</div>
            {[
              "All prices shown are exact based on your selections. Final confirmation happens after a free consultation.",
              "Domain registration and web hosting fees are not included and are paid directly by the client.",
              "50% downpayment is required to start the project. Remaining 50% is due upon completion and turnover.",
              "Source code and all project files are fully turned over upon complete payment.",
              "Changes to scope or features after project kick-off may require additional costing.",
            ].map((n, i) => (
              <div key={i} className="note-item">
                <div className="note-dot" />
                <span>{n}</span>
              </div>
            ))}
          </div>
        </div>

        <footer
          style={{
            background: "#040610",
            borderTop: "1px solid rgba(255,255,255,0.06)",
          }}
        >
          <div className="pkg-footer">
            <div className="pkg-footer-brand">
              MDS Software Development Services
            </div>
            <div className="pkg-footer-right">
              📧{" "}
              <a href="mailto:mdstechservices.info@gmail.com">
                mdstechservices.info@gmail.com
              </a>{" "}
              · © 2025 All rights reserved.
            </div>
          </div>
        </footer>
      </div>
    </PageTransition>
  );
}